// @ts-strict-ignore

import type { I18nT } from "@lib/dashboardI18n";
import type { FeatureName } from "@lib/featureFlipper";
import type { SHARED_CONSTANTS_PERMISSION_INFOS } from "@lib/shared_constants";
import sharedConstants from "@lib/shared_constants";

import type {
  Permission,
  PermissionAction,
  PermissionResource,
  PermissionResourceGroup,
  PermissionConfig,
  PermissionValues,
} from "./types";

const { PERMISSION_NAMES, PERMISSION_INFOS, PERMISSION_SCOPES, REALTIME_NOTIFIER } = sharedConstants;

//#region type unions
// you should prefer using the type unions instead of the re-exported constants in
// environments where you need type safety

//#region PermissionScope

/**
 * dict to describe a permission scope. A permission scope denotes the taxonomical level
 * of the thing to which you are giving permission, so Company scope affects all objects
 * in a company, AppGroup scope affects all objects in an app group, etc. etc.
 */
const permissionScopes = {
  company: 0,
  appGroup: 1,
  /** also known as "territories", but we should be preferring "team" everywhere */
  team: 2,
} as const;

/**
 * workspacePermissions is the set of permissions that are valid for workspaces.
 */
export const workspacePermissions: ReadonlySet<PermissionValues> = Object.freeze(
  new Set(
    sharedConstants.PERMISSIONS.PERMISSIONS_BY_SCOPE.appGroup.map((camelCaseName) => {
      const permissionValue = sharedConstants.PERMISSIONS.PERMISSIONS_BY_VALUE[camelCaseName];
      if (permissionValue === null || permissionValue === undefined) {
        throw new Error(`could not find permission value for ${camelCaseName}`);
      }
      return permissionValue;
    })
  )
);

type PermissionName = keyof SHARED_CONSTANTS_PERMISSION_INFOS;

type PermissionInfo = {
  value: PermissionValues;
  parent_permission?: PermissionName;
  prerequisite_permissions?: PermissionName[];
};
/**
 * A map from permission values (names) to values for all prerequisites permissions
 * (including parent permission) for that permission.
 */
export const PERMISSION_VALUE_TO_PREREQS_MAP = (() => {
  const map = new Map<PermissionValues, PermissionValues[]>();
  const permissionInfos = PERMISSION_INFOS as unknown as Record<PermissionValues, PermissionInfo>;
  Object.values(permissionInfos).forEach((permInfo) => {
    const prereqs: PermissionValues[] = [];
    if (permInfo.parent_permission) {
      const parent = PERMISSION_INFOS[permInfo.parent_permission];
      if (parent) {
        prereqs.push(parent.value as PermissionValues);
      }
    }
    if (permInfo.prerequisite_permissions) {
      permInfo.prerequisite_permissions.forEach((prereqKey) => {
        const prereq = PERMISSION_INFOS[prereqKey];
        if (prereq) {
          prereqs.push(prereq.value as PermissionValues);
        }
      });
    }
    if (prereqs.length > 0) {
      map.set(permInfo.value as PermissionValues, prereqs);
    }
  });
  return map;
})();

/**
 * Type union to describe the permission scopes
 * @note This is what you should use when you are typing function args, values, et al.
 */
export type PermissionScope = keyof typeof permissionScopes;
export type PermissionScopeValues = (typeof permissionScopes)[PermissionScope];
/**
 * This is a mapping of permission scope value => permission scope name
 *
 * {
 *   0: "company",
 *   1: "appGroup",
 *   2: "team",
 * }
 */
export type PermissionScopeReverseLookup = {
  [P in PermissionScopeValues]: {
    [K in PermissionScope]: (typeof permissionScopes)[K] extends P ? K : never;
  }[PermissionScope];
};

const permissionScopeLookup = (Object.entries(permissionScopes) as [PermissionScope, number][]).reduce(
  (acc, [key, value]) => {
    acc[value] = key;
    return acc;
  },
  {} as PermissionScopeReverseLookup
);

/**
 * determines the permission scope of given value
 * @note this should only be used for getting a value from an endpoint. Use the type
 *   union PermissionScope to represent values internally
 */
export const getPermissionScope = (value: PermissionScopeValues): PermissionScope | null => {
  return permissionScopeLookup[value] || null;
};

/**
 * method to get the underlying value of the given permission scope
 * @note this should only be used for passing a value back to an endpoint. Use the type
 *   union PermissionScope to represent values internally
 */
export const getPermissionScopeValue = <T extends PermissionScope>(scope: T): (typeof permissionScopes)[T] => {
  return permissionScopes[scope];
};

//#endregion

//#region Permission

/**
 * map to describe the things to which you are giving permission, regardless of scope
 */
export const permissions = sharedConstants.PERMISSIONS.PERMISSIONS_BY_VALUE;

/**
 * A map of scope to allowed permissions
 */
export const permissionsByScope = sharedConstants.PERMISSIONS.PERMISSIONS_BY_SCOPE;

export type PermissionByScopeMap = keyof typeof sharedConstants.PERMISSIONS.PERMISSIONS_BY_SCOPE;
export type PermissionsByScope = typeof permissionsByScope;

const permissionLookup = (Object.entries(permissions) as [Permission, number][]).reduce(
  (acc, [key, value]) => {
    acc[value] = key;
    return acc;
  },
  {} as Record<number, Permission>
);

export const getPermission = (value: number): Permission | null => {
  return permissionLookup[value] || null;
};

/**
 * method to get the underlying value of the given permission
 * @note this should only be used for passing a value back to an endpoint. Use the type
 *   union Permission to represent values internally
 */
export const getPermissionValue = <T extends Permission>(permission: T): (typeof permissions)[T] => {
  return permissions[permission];
};

//#endregion

//#endregion

/**
 * @deprecated use the type union `Permission` to represent these values internally
 */
export const PERMISSIONS = PERMISSION_NAMES;
/**
 * @deprecated use the type union `PermissionScope` to represent these values internally
 */
export const SCOPES = PERMISSION_SCOPES;
export const OPTIONS = {
  ADMIN: "Administrator",
  NON_ADMIN: "Limited",
} as const;
export type SelectedPermissionOptions = (typeof OPTIONS)[keyof typeof OPTIONS];
export const SELECT_OPTIONS = [OPTIONS.ADMIN, OPTIONS.NON_ADMIN];
export const MANAGE_APP_GROUP_PERMISSIONS = [
  PERMISSIONS.MANAGE_TAGS,
  PERMISSIONS.MANAGE_TERRITORIES,
  PERMISSIONS.MANAGE_EMAIL_SETTINGS,
  PERMISSIONS.MANAGE_APPS,
  PERMISSIONS.MANAGE_CUSTOM_ATTRIBUTES_EVENTS_PRODUCTS,
];

export const PUSHER_CONFIG = {
  privateChannelPrefix: REALTIME_NOTIFIER.channel.private_prefix,
  globalEventsChannel: REALTIME_NOTIFIER.channel.global_events,
  developerSelfChannelPrefix: REALTIME_NOTIFIER.channel.developer_self_prefix,
  companyChannelPrefix: REALTIME_NOTIFIER.channel.company_prefix,
  forceRefreshEvent: REALTIME_NOTIFIER.event.force_refresh,

  reloadOnNextNavigationEvent: REALTIME_NOTIFIER.event.reload_page_on_next_request,
  campaignStartedSending: REALTIME_NOTIFIER.event.campaign_started_sending,
  segmentActualStats: REALTIME_NOTIFIER.event.segment_actual_stats,
  segmentInsightsActualStats: REALTIME_NOTIFIER.event.segment_insights_actual_stats,
  campaignActualAudienceStats: REALTIME_NOTIFIER.event.campaign_actual_audience_stats,
  campaignConversionCorrelation: REALTIME_NOTIFIER.event.campaign_conversion_correlation,
  campaignFinishedSending: REALTIME_NOTIFIER.event.campaign_finished_sending,
  genericPusherRequest: REALTIME_NOTIFIER.event.generic_pusher_request,
  segmentFinishedExporting: REALTIME_NOTIFIER.event.segment_finished_exporting,
  csvFinishedImporting: REALTIME_NOTIFIER.event.csv_finished_importing,
  customAttributeSchemaGenerationFinished: REALTIME_NOTIFIER.event.custom_attribute_schema_generation_finished,
  blocklistingStatusUpdated: REALTIME_NOTIFIER.event.blocklisting_status_updated,
  itemUpserted: REALTIME_NOTIFIER.event.item_upserted,
  arbitraryError: REALTIME_NOTIFIER.event.arbitrary_error,
  retentionV3Stats: REALTIME_NOTIFIER.event.retention_report,
  funnelReport: REALTIME_NOTIFIER.event.funnel_report,
  conversionReport: REALTIME_NOTIFIER.event.conversion_report,
  expressiveReport: REALTIME_NOTIFIER.event.expressive_report,
  dashboardBuilder: REALTIME_NOTIFIER.event.dashboard_builder,
  reportBuilder: REALTIME_NOTIFIER.event.report_builder,
  exportType: {
    facebook: REALTIME_NOTIFIER.export_type.facebook,
    csv: REALTIME_NOTIFIER.export_type.csv,
  },
  segmentCohortRefreshed: REALTIME_NOTIFIER.event.segment_cohort_refreshed,
  catalogUpdated: REALTIME_NOTIFIER.event.catalog_updated,
  catalogItemUpdated: REALTIME_NOTIFIER.event.catalog_item_updated,
  catalogCsvGenerated: REALTIME_NOTIFIER.event.catalog_csv_generated,
  testSendUserUpdateCompleted: REALTIME_NOTIFIER.event.test_send_user_update_completed,
  usersMergeSucceeded: REALTIME_NOTIFIER.event.users_merge_succeeded,
  usersMergeFailed: REALTIME_NOTIFIER.event.users_merge_failed,
  duplicateUsersMergeCompleted: REALTIME_NOTIFIER.event.duplicate_users_merge_completed,
  integrationBridgeUpdated: REALTIME_NOTIFIER.event.integration_bridge_updated,
  shopifyCatalogItemErrored: REALTIME_NOTIFIER.event.shopify_catalog_item_errored,
  shopifyCatalogStorageLimitReached: REALTIME_NOTIFIER.event.shopify_catalog_storage_limit_reached,
  shopifyItemErrorCsvExportCompleted: REALTIME_NOTIFIER.event.shopify_item_error_csv_export_completed,
  segmentStatsProgressUpdate: REALTIME_NOTIFIER.event.segment_stats_progress_update, // deprecate with generic_async_request_params FF deletion
  segmentStatsConcurrencyUpdate: REALTIME_NOTIFIER.event.segment_stats_concurrency_update, // ^^
  segmentStatsFailureUpdate: REALTIME_NOTIFIER.event.segment_stats_failure_update, // ^^
  segmentStatsCancellationUpdate: REALTIME_NOTIFIER.event.segment_stats_cancellation_update, // ^^
  entityStatsProgressUpdate: REALTIME_NOTIFIER.event.entity_stats_progress_update,
  entityStatsConcurrencyUpdate: REALTIME_NOTIFIER.event.entity_stats_concurrency_update,
  entityStatsFailureUpdate: REALTIME_NOTIFIER.event.entity_stats_failure_update,
  entityStatsCancellationUpdate: REALTIME_NOTIFIER.event.entity_stats_cancellation_update,
  shopifyBridgeUpdated: REALTIME_NOTIFIER.event.shopify_bridge_updated,
  sageAssistantStream: REALTIME_NOTIFIER.event.sage_assistant_stream,
  knowledgeSourceSyncCompleted: REALTIME_NOTIFIER.event.knowledge_source_sync_completed,
};

export const PERMISSION_RESOURCE_GROUP_DISPLAY_NAMES = (
  t: I18nT<"permission-resource-groups">
): Record<PermissionResourceGroup, string> => {
  return {
    messaging: t("messaging", "Messaging"),
    audience: t("audience", "Audience"),
    template: t("template", "Template"),
    partner_integrations: t("partner-integrations", "Partner integrations"),
    data_settings: t("data-settings", "Data settings"),
    analytics: t("analytics", "Analytics"),
    ai_decisioning: t("ai-decisioning", "AI decisioning"),
    agent_console: t("agent-console", "Agent Console"),
    settings: t("settings", "Settings"),
    other: t("other", "Other"),
  };
};

export const PERMISSION_ACTION_DISPLAY_NAMES = (
  t: I18nT<"permission-actions">
): Record<PermissionAction, string> => {
  return {
    allowlist: t("allowlist", "Allowlist"),
    approve: t("approve", "Approve"),
    archive: t("archive", "Archive"),
    blocklist: t("blocklist", "Blocklist"),
    delete: t("delete", "Delete"),
    edit: t("edit", "Edit"),
    export: t("export", "Export"),
    import: t("import", "Import"),
    merge: t("merge", "Merge"),
    publish: t("publish", "Publish"),
    replace: t("replace", "Replace"),
    suspend: t("suspend", "Suspend"),
    view: t("view", "View"),
    // Additional Actions that aren't part of the current Permission Guidance. Require review (ACTIONS_TO_REVIEW)!!!
    send: t("send", "Send"),
    admin: t("admin", "Admin"),
    launch: t("launch", "Launch"),
    manage: t("manage", "Manage"),
    search: t("search", "Search"),
  };
};

export const PERMISSION_RESOURCE_DISPLAY_NAMES = (
  t: I18nT<"permission-resources">
): Record<PermissionResource, string> => {
  return {
    api_identifiers: t("api-identifiers", "API Identifiers"),
    api_usage_alerts: t("api-usage-alerts", "API Usage Alerts"),
    api_usage_data: t("api-usage-data", "API Usage Data"),
    api_limits: t("api-limits", "API Limits"),
    decisioning_studio_agents: t("decisioning-studio-agents", "Decisioning Studio Agents"),
    decisioning_studio_guardrails: t("decisioning-studio-guardrails", "Decisioning Studio Guardrails"),
    app_usage: t("app-usage", "App Usage"),
    apps: t("apps", "Apps"),
    billing: t("billing", "Billing"),
    canvas_templates: t("canvas-templates", "Canvas Templates"),
    custom_agents: t("custom-agents", "Agent Console"),
    custom_attributes_events_products: t(
      "custom-attributes-events-products",
      "Custom Attributes, Events, Products"
    ),
    custom_attributes_marked_as_pii: t("custom-attributes-marked-as-pii", "Custom Attributes Marked As PII"),
    custom_event_property_segmentation: t(
      "custom-event-property-segmentation",
      "Custom Event Property Segmentation"
    ),
    dashboard_reports: t("dashboard-reports", "Dashboard Reports"),
    developer_console: t("developer-console", "Developer Console"),
    duplicate_users: t("duplicate-users", "Duplicate Users"),
    external_integrations: t("external-integrations", "External Integrations"),
    field_level_encryption: t("field-level-encryption", "Field Level Encryption"),
    free_trial_developers: t("free-trial-developers", "Free Trial Developers"),
    conversational_workflows: t("conversational-workflows", "Conversational Workflows"),
    conversational_routing: t("conversational-routing", "Conversational Routing"),
    knowledge_sources: t("knowledge-sources", "Knowledge Sources"),
    landing_page_templates: t("landing-page-templates", "Landing Page Templates"),
    media_library_assets: t("media-library-assets", "Media Library Assets"),
    meta_bids: t("meta-bids", "Meta Bids"),
    purchase_property_segmentation: t("purchase-property-segmentation", "Purchase Property Segmentation"),
    subscription_groups: t("subscription-groups", "Subscription Groups"),
    transformations: t("transformations", "Transformations"),
    data: t("data", "Data"),
    system: t("system", "System"),
    banners: t("banners", "Banners"),
    banner_templates: t("banner-templates", "Banner Templates"),
    content_blocks: t("content-blocks", "Content Blocks"),
    global_rate_limits: t("global-rate-limits", "Messaging Rate Limits"),
    global_control_group: t("global-control-group", "Global Control Group"),
    placements: t("placements", "Placements"),
    support: t("support", "Support"),
    canvas: t("canvas", "Canvas"),
    campaigns: t("campaigns", "Campaigns"),
    feature_flags: t("feature-flags", "Feature flags"),
    landing_pages: t("landing-pages", "Landing pages"),
    segments: t("segments", "Segments"),
    user_data: t("user-data", "User Data"),
    user_deletion_records: t("user-deletion-records", "User Deletion Records"),
    user_merge_records: t("user-merge-records", "User Merge Records"),
    locations: t("locations", "Locations"),
    subscriptions: t("subscriptions", "Subscriptions"),
    message_structure: t("message-structure", "Message Structure"),
    message_body: t("message-body", "Message Body"),
    creative_assets: t("creative-assets", "Creative Assets"),
    technology_partners: t("technology-partners", "Technology Partners"),
    currents_integrations: t("currents-integrations", "Currents Integrations"),
    catalogs: t("catalogs", "Catalogs"),
    custom_events: t("custom-events", "Custom Events"),
    data_transformation: t("data-transformation", "Data Transformation"),
    products: t("products", "Products"),
    custom_attributes: t("custom-attributes", "Custom Attributes"),
    cloud_data_ingestion: t("cloud-data-ingestion", "Cloud Data Ingestion"),
    pii: t("pii", "PII"),
    promotion_codes: t("promotion-codes", "Promotion Codes"),
    api_keys: t("api-keys", "API Keys"),
    event_user_log: t("event-user-log", "Event User Log"),
    message_activity_log: t("message-activity-log", "Message Activity Log"),
    internal_groups: t("internal-groups", "Internal Groups"),
    tags: t("tags", "Tags"),
    usage_data: t("usage-data", "Usage Data"),
    app_settings: t("app-settings", "App Settings"),
    dashboard_users: t("dashboard-users", "Dashboard Users"),
    email_settings: t("email-settings", "Email Settings"),
    multi_language_settings: t("multi-language-settings", "Multi Language Settings"),
    preference_centers: t("preference-centers", "Preference Centers"),
    teams: t("teams", "Teams"),
    email_templates: t("email-templates", "Email Templates"),
    email_link_templates: t("email-link-templates", "Email Link Templates"),
    iam_templates: t("iam-templates", "IAM Templates"),
    webhook_templates: t("webhook-templates", "Webhook Templates"),
    sdk_debugger: t("sdk-debugger", "SDK Debugger"),
    cross_channel_profile_linking: t("cross-channel-profile-linking", "Cross Channel Profile Linking"),
    operator: t("operator", "Operator"),
    push_settings: t("push-settings", "Push Settings"),
    message_prioritization: t("message-prioritization", "Message Prioritization"),
    frequency_caps: t("frequency-caps", "Frequency Caps"),
    whatsapp_message_templates: t("whatsapp-message-templates", "Whatsapp Message Templates"),
    support_tickets: t("support-tickets", "Support Tickets"),
    decisioning_studio_conversion_event: t(
      "decisioning-studio-conversion-event",
      "Decisioning Studio Conversion Event"
    ),
    decisioning_studio_audience: t("decisioning-studio-audience", "Decisioning Studio Audience"),
    whatsapp_settings: t("whatsapp-settings", "WhatsApp Settings"),
    whatsapp_flows: t("whatsapp-flows", "WhatsApp Flows"),
    whatsapp_catalog: t("whatsapp-catalog", "WhatsApp Catalog"),
    whatsapp_message_templates_from_meta: t(
      "whatsapp-message-templates-from-meta",
      "WhatsApp Message Templates From Meta"
    ),
    audience_sync_settings: t("audience-sync-settings", "Audience Sync Settings"),
    decisioning_studio_timing: t("decisioning-studio-timing", "Decisioning Studio Timing"),
    decisioning_studio_action_banks: t("decisioning-studio-action-banks", "Decisioning Studio Action Banks"),
    decisioning_studio_creative_variants: t(
      "decisioning-studio-creative-variants",
      "Decisioning Studio Creative Variants"
    ),
    message_archiving_settings: t("message-archiving-settings", "Message Archiving Settings"),
    quiet_hours_settings: t("quiet-hours-settings", "Quiet Hours Settings"),
    import_users: t("import-users", "Import Users"),
    audience_population: t("audience-population", "Audience Population"),
    custom_objects: t("custom-objects", "Custom Objects"),
  };
};

export const PERMISSION_CONFIGS = (
  t: I18nT<"permission-constants">,
  featureOn?: (featureName: FeatureName) => boolean
): PermissionConfig[] => [
  {
    // special permission which is granted differently and doesn't need resource or action
    permission: PERMISSIONS.ADMIN,
    display_name: t("admin", "Admin"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: true,
    definition: t(
      "admin-definition",
      "Allows users to access all available features. This is the default setting for all new users. Can update company settings (company name and time zone), which limited users cannot do."
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.BASIC_READ_ONLY,
    dynamic_display_name: (featureOn: (feature: FeatureName) => boolean, locale: Intl.Locale): string => {
      const objectTokens = [
        t("campaigns-token", "Campaigns"),
        featureOn("journeys") && t("canvas-token", "Canvases"),
        t("cards-token", "Cards"),
        t("content-blocks-token", "Content Blocks"),
        featureOn("feature_flags") && t("feature-flags-token", "Feature Flags"),
        !featureOn("ut_granular_permission_segments") && t("segment-token", "Segments"),
        t("media-library-token", "Media Library"),
        featureOn("locations") && t("locations", "Locations"),
        t("preference-center-token", "Preference Centers"),
      ].filter(Boolean);
      const objects = new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(objectTokens);
      return t("access-campaigns-cards-segments-medialib-others", "Access { objects }", {
        objects: objects,
      });
    },
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: false }],
    helper_text: null,
    enabled_for_territories: true,
    definition: t(
      "basic-read-only-definition",
      "Allows users to view campaign and Canvas performance metrics, create and duplicate drafts of campaigns and Canvases, edit campaign and Canvas drafts and templates, view drafts of segments, templates and media, create templates, upload media, create or update promotion code lists, view engagement reports, and view global message settings in the dashboard. However, users with this permission cannot pause or edit existing live content."
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.SEND_CAMPAIGNS,
    display_name: t("send-campaigns-canvases", "Send Campaigns, Canvases"),
    feature_flipper: [{ name: "journeys", match_value: true }],
    helper_text: null,
    enabled_for_territories: true,
    definition: t(
      "send-campaigns-definition",
      "Allows users to edit, archive, and stop campaigns, and create campaigns."
    ),
    sensitive_tooltip_text: t("send-campaigns-canvases-sensitive-tooltip-text", "Potential for accidental sends"),
  },
  {
    // legacy permission
    permission: PERMISSIONS.SEND_CAMPAIGNS,
    display_name: t("send-campaign", "Send Campaigns"),
    feature_flipper: [{ name: "journeys", match_value: false }],
    helper_text: null,
    definition: t(
      "send-campaigns-canvases-definition",
      "Allows users to edit, archive, and stop campaigns and Canvases, create campaigns, and launch Canvases."
    ),
    enabled_for_territories: true,
    sensitive_tooltip_text: t("send-campaigns-sensitive-tooltip-text", "Potential for accidental sends"),
  },
  {
    // legacy permission
    permission: permissions.launchAndManageContentBlocks,
    display_name: t("launch-and-manage-content-blocks", "Launch and Manage Content Blocks"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: false },
      { name: "granular_permissions_content_blocks", match_value: false },
    ],
    helper_text: null,
    enabled_for_territories: true,
    definition: t(
      "launch-and-manage-content-blocks-definition",
      "Allows users to launch and manage Content Blocks."
    ),
  },
  {
    permission: permissions.manageCompanySettings,
    display_name: t("manage-company-settings", "Manage Company Settings"),
    feature_flipper: [],
    helper_text: null,
    enabled_for_territories: false,
    definition: t("manage-company-settings-definition", "Allows users to manage company settings."),
  },
  {
    permission: PERMISSIONS.VIEW_SEGMENTS,
    display_name: t("view-segments", "View Segments"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ut_granular_permission_segments", match_value: true },
    ],
    helper_text: null,
    enabled_for_territories: true,
    resource: "segments",
    action: "view",
    granular_permission_of: PERMISSIONS.BASIC_READ_ONLY,
    definition: t("view-segments-definition", "View segments without making changes."),
  },
  {
    permission: PERMISSIONS.ARCHIVE_SEGMENTS,
    display_name: t("archive-segments", "Archive Segments"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ut_granular_permission_segments", match_value: true },
    ],
    helper_text: null,
    enabled_for_territories: true,
    resource: "segments",
    action: "archive",
    granular_permission_of: PERMISSIONS.EDIT_SEGMENTS,
    definition: t("archive-segments-definition", "Move segments to archive."),
  },
  {
    permission: PERMISSIONS.EDIT_SEGMENTS,
    display_name: t("edit-segments", "Edit Segments"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: true,
    resource: "segments",
    action: "edit",
    definition: t("edit-segments-definition", "Create and update Segments."),
  },
  {
    permission: PERMISSIONS.EXPORT_DATA,
    display_name: t("export-user", "Export User Data"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: true,
    sensitive_tooltip_text: t("view-pii-sensitive-tooltip-text", "Allow access to sensitive end user information"),
    resource: "user_data",
    action: "export",
    definition: t("export-user-definition", "Download users from the dashboard."),
  },
  {
    permission: PERMISSIONS.VIEW_PII,
    display_name: t("view-pii", "View PII"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: featureOn?.("view_pii_territory_scope") ?? false,
    sensitive_tooltip_text: t("view-pii-sensitive-tooltip-text", "Allow access to sensitive end user information"),
    resource: "pii",
    action: "view",
    definition: t(
      "view-pii-definition",
      "Allows users to view personally identifiable information (PII) fields as defined by your company within the dashboard. Users can also view PII fields in the Preview as a User tab of message previews.\n\nYou need this permission to use Query Builder, because it allows direct access to some customer data."
    ),
  },
  {
    permission: PERMISSIONS.VIEW_CUSTOM_ATTRIBUTES_MARKED_AS_PII,
    display_name: t("view-custom-attributes-marked-as-pii", "View Custom Attributes Marked as PII"),
    feature_flipper: [{ name: "pii_custom_attributes", match_value: true }],
    helper_text: null,
    enabled_for_territories: false,
    resource: "custom_attributes_marked_as_pii",
    sensitive_tooltip_text: t("view-pii-sensitive-tooltip-text", "Allow access to sensitive end user information"),
    action: "view",
    definition: t(
      "view-custom-attributes-marked-as-pii-definition",
      "Allows non-admin users to view custom attributes that contain sensitive information and are marked as personally identifiable information (PII)."
    ),
  },
  {
    permission: PERMISSIONS.SEARCH_USERS,
    display_name: t("view-user-profiles-pii", "View User Profiles (PII Redacted)"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: true,
    resource: "user_data",
    sensitive_tooltip_text: t("view-pii-sensitive-tooltip-text", "Allow access to sensitive end user information"),
    action: "search",
    definition: t(
      "view-user-profiles-pii-definition",
      "Allows users to view user profiles that contain fields your company has defined as personally identifiable information (PII), but redacts the PII fields.\n\nYou need this permission to use the user search tool."
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.MANAGE_DEVELOPERS_AND_PERMISSIONS,
    display_name: t("manage-dashboard-users", "Manage Dashboard Users"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: false },
      { name: "clip_granular_permission_manage_developers_and_permissions", match_value: false },
    ],
    helper_text: null,
    enabled_for_territories: true,
    definition: t(
      "manage-dashboard-users-definition",
      "Allows non-admins the ability to view, edit, and manage the Company Users page, and manage the dashboard users in their workspace by modifying the permissions of any user, including themselves. Users with this permission can't delete users (only administrators can delete users)."
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.MANAGE_MEDIA_LIBRARY,
    display_name: t("manage-media-lib", "Manage Media Library Assets"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: false },
      { name: "granular_permissions_media_library", match_value: false },
    ],
    helper_text: null,
    enabled_for_territories: true,
    definition: t(
      "manage-media-lib-definition",
      "Allows users to create, update, and delete media library assets."
    ),
  },
  {
    permission: PERMISSIONS.VIEW_APP_USAGE,
    display_name: t("view-usage", "View Usage Data"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: false,
    resource: "app_usage",
    action: "view",
    definition: t(
      "view-usage-definition",
      "Allows users to view app usage, including the channel performance dashboards."
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.IMPORT_AND_UPDATE_USERS,
    display_name: t("import-update-user", "Import And Update User Data"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: false },
      { name: "ingestion_granular_permission_import_and_update_users", match_value: false },
    ],
    helper_text: null,
    enabled_for_territories: false,
    definition: t(
      "import-update-user-definition",
      "Allows users to import CSV and update files of app users as well as view the User Import page. This also allows you to edit the subscription status of a user and their subscription group opt-in/opt-out rules."
    ),
  },
  {
    permission: permissions.viewBilling,
    display_name: t("view-billing", "View Billing Details"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: false,
    resource: "billing",
    action: "view",
    definition: t("view-billing-definition", "Allows users to view subscriptions and billing."),
  },
  {
    // legacy permission
    permission: PERMISSIONS.DEVELOPER_CONSOLE,
    display_name: t("access-dev-console", "Access Dev Console"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: false,
    definition: t(
      "access-dev-console-definition",
      "Allows full access to the following settings and logs:\n- API Keys\n- Internal Groups\n- Message Activity Log\n- Event User Log"
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.EXTERNAL_INTEGRATIONS,
    display_name: t("manage-ext-integ", "Manage External Integrations"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: false,
    resource: "external_integrations",
    action: "edit",
    definition: t(
      "manage-ext-integ-definition",
      "Allows access to all tabs under Technology Partners, ability to sync Braze with other platforms, and access to manage Cloud Data Ingestion."
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.MANAGE_APPS,
    display_name: t("manage-apps", "Manage Apps"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: false },
      { name: "feature_flags_granular_permission_manage_apps", match_value: false },
      { name: "push_granular_permission_manage_apps", match_value: false },
    ],
    helper_text: null,
    enabled_for_territories: false,
    definition: t("manage-apps-definition", "Allows users to edit App Settings."),
  },
  {
    // legacy permission
    permission: PERMISSIONS.MANAGE_TERRITORIES,
    display_name: t("manage-teams", "Manage Teams"),
    feature_flipper: [
      { name: "territories", match_value: true },
      { name: "clip_granular_permissions_all", match_value: false },
    ],
    helper_text: null,
    enabled_for_territories: false,
    definition: t(
      "manage-teams-definition",
      "Allows users to manage Internal Teams. The ability to select this permission depends on your contract with Braze."
    ),
  },
  {
    // legacy permission
    permission: permissions.manageCustomAttributesEventsProducts,
    display_name: t("manage-events-attr-purchases", "Manage Events, Attributes, Purchases"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: false },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: false },
    ],
    helper_text: null,
    enabled_for_territories: false,
    definition: t(
      "manage-events-attr-purchases-definition",
      "Allows users to edit custom attributes (users without this capability can still view custom attributes), edit and view properties of custom events, and edit and view properties of products under Data Settings."
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.MANAGE_TAGS,
    display_name: t("manage-tags", "Manage Tags"),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: false }],
    helper_text: null,
    enabled_for_territories: false,
    definition: t(
      "manage-tags-definition",
      "Allows users to edit or delete tags (under Tag Management). You do not need this permission to add tags to campaigns or segments."
    ),
  },
  {
    // legacy permission
    permission: PERMISSIONS.MANAGE_EMAIL_SETTINGS,
    display_name: t("manage-email-settings", "Manage Email Settings"),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: false }],
    helper_text: null,
    enabled_for_territories: false,
    definition: t(
      "manage-email-settings-definition",
      "Allows users to save email configuration changes (Settings > Email Preferences)."
    ),
  },
  {
    permission: PERMISSIONS.MANAGE_SUBSCRIPTION_GROUPS,
    dynamic_display_name: (featureOn: (feature: FeatureName) => boolean, _locale: Intl.Locale): string => {
      return featureOn("email_granular_permission_manage_email_settings")
        ? t("edit-subscriptions", "Edit Subscriptions")
        : // legacy permission renaming
          t("manage-subscr-groups", "Manage Subscription Groups");
    },
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: false,
    resource: "subscription_groups",
    action: "edit",
    definition: t("manage-subscription-groups-definition", "Create and update subscription groups."),
  },
  {
    permission: permissions.viewTransformations,
    display_name: t("view-transformations", "View Data Transformation"),
    feature_flipper: [{ name: "data_transformation_permissions", match_value: true }],
    helper_text: null,
    enabled_for_territories: false,
    resource: "transformations",
    action: "view",
    definition: t("view-transformations-definition", "View data transformations."),
  },
  {
    permission: permissions.manageTransformations,
    display_name: t("manage-transformations", "Edit Data Transformation"),
    feature_flipper: [{ name: "data_transformation_permissions", match_value: true }],
    helper_text: null,
    enabled_for_territories: false,
    resource: "transformations",
    action: "edit",
    definition: t("manage-transformations-definition", "Create and update data transformations."),
  },
  {
    // legacy permission
    permission: permissions.manageFeatureFlags,
    display_name: t("manage-feature-flags", "Manage Feature Flags"),
    feature_flipper: [
      { name: "feature_flags", match_value: true },
      { name: "feature_flags_permissions_management", match_value: false },
      { name: "clip_granular_permissions_all", match_value: false },
    ],
    helper_text: null,
    definition: null,
    enabled_for_territories: false,
  },
  {
    permission: permissions.managePreferenceCenters,
    display_name: t("manage-preference-centers", "Launch Preference Centers"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: false,
    resource: "preference_centers",
    action: "launch",
    definition: t(
      "manage-preference-centers-definition",
      "Make a draft Preference Center active or update an existing one."
    ),
  },
  {
    permission: permissions.approveDenyCampaigns,
    dynamic_display_name: (featureOn: (feature: FeatureName) => boolean, _locale: Intl.Locale): string => {
      return featureOn("mx_permissions_canvas_templates_approve_deny")
        ? t("approve-campaigns", "Approve Campaigns")
        : t("approve-deny-campaigns", "Approve and Deny Campaigns");
    },
    feature_flipper: null,
    helper_text: t(
      "helper-text-approve-deny-campaigns",
      "Approval Workflow for campaigns must be enabled for this permission to take an effect"
    ),
    enabled_for_territories: true,
    resource: "campaigns",
    action: "approve",
    definition: t(
      "approve-deny-campaigns-definition",
      "Allows users to approve or deny campaigns. The approval workflow for campaigns must be turned on for this permission to apply. This setting is currently in early access. Contact your account manager if you're interested in participating in the early access."
    ),
  },
  {
    permission: permissions.approveDenyCanvases,
    dynamic_display_name: (featureOn: (feature: FeatureName) => boolean, _locale: Intl.Locale): string => {
      return featureOn("mx_permissions_canvas_templates_approve_deny")
        ? t("approve-canvases", "Approve Canvases")
        : t("approve-deny-canvases", "Approve and Deny Canvases");
    },
    feature_flipper: null,
    helper_text: t(
      "helper-text-approve-deny-canvases",
      "Approval Workflow for Canvases must be enabled for this permission to take an effect"
    ),
    enabled_for_territories: true,
    resource: "canvas",
    action: "approve",
    definition: t(
      "approve-deny-canvases-definition",
      "Allows users to approve or deny Canvases. The approval workflow for Canvases must be turned on for this permission to apply."
    ),
  },
  {
    // legacy permission
    permission: permissions.manageMultiLanguageSettings,
    display_name: t("manage-multi-language-settings", "Edit Localization Settings"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: false },
      { name: "granular_permissions_multi_language", match_value: false },
    ],
    helper_text: null,
    definition: null,
    enabled_for_territories: false,
  },
  {
    // legacy permission
    permission: permissions.manageCatalogsDashboardPermission,
    display_name: t("manage-catalogs-dashboard-permission", "Manage Catalogs Dashboard Permission"),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: false }],
    helper_text: null,
    enabled_for_territories: false,
    definition: t("manage-catalogs-dashboard-permission-definition", "Allows users to create and manage catalogs."),
  },
  {
    permission: permissions.manageFieldLevelEncryption,
    dynamic_display_name: (featureOn: (feature: FeatureName) => boolean, _locale: Intl.Locale): string => {
      return featureOn("email_granular_permission_manage_email_settings")
        ? t("edit-identifier-field-level-encryption", "Edit Identifier Field-Level Encryption")
        : t("manage-identifier-field-level-encryption", "Manage Identifier Field-Level Encryption");
    },
    feature_flipper: [{ name: "pii_fields_encryption", match_value: true }],
    helper_text: null,
    enabled_for_territories: false,
    resource: "field_level_encryption",
    action: "edit",
    definition: t(
      "manage-identifier-field-level-encryption-definition",
      "Enable and update Field-Level Encryption settings."
    ),
  },
  {
    permission: permissions.publishLandingPages,
    display_name: t("publish-landing-pages-permission-display-name", "Publish Landing Pages"),
    feature_flipper: [{ name: "landing_pages", match_value: true }],
    helper_text: t(
      "helper-text-publish-landing-pages-permission",
      "Allows users to publish, modify, and unpublish landing pages"
    ),
    enabled_for_territories: false,
    resource: "landing_pages",
    action: "publish",
    definition: t("publish-landing-pages-definition", "Allows users to publish landing pages."),
  },
  {
    permission: permissions.createLandingPageDrafts,
    display_name: t("edit-landing-page-drafts-permission-display-name", "Edit Landing Page Drafts"),
    feature_flipper: [{ name: "landing_pages", match_value: true }],
    helper_text: t(
      "helper-text-edit-landing-page-drafts-permission",
      "Allows users to create and edit landing page drafts, but not publish them"
    ),
    enabled_for_territories: false,
    resource: "landing_pages",
    action: "edit",
    definition: t("edit-landing-page-drafts-definition", "Allows users to create and save landing page drafts."),
  },
  {
    permission: permissions.readLandingPages,
    display_name: t("view-landing-pages-permission-display-name", "View Landing Pages"),
    feature_flipper: [{ name: "landing_pages", match_value: true }],
    helper_text: t(
      "helper-text-view-landing-pages-permission",
      "Allows users to view landing pages, but not modify or publish them"
    ),
    enabled_for_territories: false,
    resource: "landing_pages",
    action: "view",
    definition: t("view-landing-pages-definition", "Allows users to access the Landing Pages page."),
  },
  {
    permission: permissions.mergeDuplicateUsers,
    display_name: t("bulk-user-merging-permission-display-name", "Merge Duplicate Users"),
    feature_flipper: [{ name: "bulk_user_merge", match_value: true }],
    helper_text: t("helper-text-bulk-user-merging", "Allows users to merge duplicate user profiles"),
    enabled_for_territories: false,
    definition: t(
      "merge-duplicate-users-definition",
      "Combine duplicate users into one user. Duplicates are removed after merging."
    ),
  },
  {
    permission: permissions.editCanvasTemplates,
    dynamic_display_name: (featureOn: (feature: FeatureName) => boolean, _locale: Intl.Locale): string => {
      return featureOn("mx_permissions_canvas_templates_approve_deny")
        ? t("edit-canvas-templates", "Edit Canvas Templates")
        : t("create-and-edit-canvas-templates-display-name", "Create and Edit Canvas Templates");
    },
    feature_flipper: null,
    helper_text: t(
      "helper-text-create-and-update-canvas-templates",
      "Allows users to create and update Canvas Templates"
    ),
    enabled_for_territories: true,
    resource: "canvas_templates",
    action: "edit",
    definition: t(
      "create-and-edit-canvas-templates-definition",
      "Allows users to create and edit Canvas templates."
    ),
  },
  {
    permission: permissions.viewCanvasTemplates,
    display_name: t("view-canvas-templates-display-name", "View Canvas Templates"),
    feature_flipper: null,
    helper_text: t("helper-text-view-canvas-templates", "Allows users to view Canvas Templates"),
    enabled_for_territories: true,
    resource: "canvas_templates",
    action: "view",
    definition: t("view-canvas-templates-definition", "Allows users to view Canvas templates."),
  },
  {
    permission: permissions.archiveCanvasTemplates,
    display_name: t("archive-canvas-templates-display-name", "Archive Canvas Templates"),
    feature_flipper: null,
    helper_text: t("helper-text-archive-canvas-templates", "Allows users to archive Canvas Templates"),
    enabled_for_territories: true,
    resource: "canvas_templates",
    action: "archive",
    definition: t("archive-canvas-templates-definition", "Allows users to archive Canvas templates."),
  },
  {
    permission: permissions.viewCurrentsIntegrations,
    display_name: t("view-currents-integrations", "View Currents Integrations"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: false,
    resource: "currents_integrations",
    action: "view",
    definition: t(
      "view-currents-integrations-definition",
      'Allows users to view all information about a Currents connection, excluding credentials. By default, users assigned the "Access Campaigns, Canvases, Cards, Content Blocks, Feature Flags, Segments, Media Library, Locations, Promotion Codes, and Preference Centers" permission are also assigned this permission.'
    ),
  },
  {
    permission: permissions.editCurrentsIntegrations,
    display_name: t("edit-currents-integrations", "Edit Currents Integrations"),
    feature_flipper: null,
    helper_text: null,
    enabled_for_territories: false,
    resource: "currents_integrations",
    action: "edit",
    definition: t(
      "edit-currents-integrations-definition",
      'Allows users to modify a Currents connection, including credentials. By default, users assigned the "External Integrations" permission are also assigned this permission.'
    ),
  },
  {
    permission: permissions.createFreeTrialDevelopers,
    display_name: t("create-free-trial-developers-permission-display-name", "Create Free Trial Developers"),
    feature_flipper: [{ name: "free_trial_add_new_user", match_value: true }],
    helper_text: t("helper-text-create-free-trial-developers", "Allows developers to create free trial developers"),
    definition: null,
    enabled_for_territories: false,
    resource: "free_trial_developers",
    action: "edit",
  },
  {
    permission: permissions.deleteFreeTrialDevelopers,
    display_name: t("delete-free-trial-developers-permission-display-name", "Delete Free Trial Developers"),
    feature_flipper: [{ name: "free_trial_add_new_user", match_value: true }],
    helper_text: t("helper-text-delete-free-trial-developers", "Allows developers to delete free trial developers"),
    definition: null,
    enabled_for_territories: false,
    resource: "free_trial_developers",
    action: "delete",
  },
  {
    permission: permissions.viewFreeTrialDevelopers,
    display_name: t("view-free-trial-developers-permission-display-name", "View Free Trial Developers"),
    feature_flipper: [{ name: "free_trial_add_new_user", match_value: true }],
    helper_text: t("helper-text-view-free-trial-developers", "Allows developers to view free trial developers"),
    definition: null,
    enabled_for_territories: false,
    resource: "free_trial_developers",
    action: "view",
  },
  {
    permission: permissions.previewDuplicateUsers,
    display_name: t("bulk-user-merge-preview-permission-display-name", "Preview Duplicate Users"),
    feature_flipper: [{ name: "bulk_user_merge_preview", match_value: true }],
    helper_text: t("helper-text-bulk-user-merge-preview", "Allows users to preview bulk user merge profiles"),
    enabled_for_territories: false,
    resource: "duplicate_users",
    action: "view",
    definition: t(
      "preview-duplicate-users-definition",
      "Allows users to preview which user profiles are duplicated."
    ),
  },
  {
    permission: permissions.updateFreeTrialDevelopers,
    display_name: t("update-free-trial-developers-permission-display-name", "Update Free Trial Developers"),
    feature_flipper: [{ name: "free_trial_add_new_user", match_value: true }],
    helper_text: t("helper-text-update-free-trial-developers", "Allows developers to update free trial developers"),
    definition: null,
    enabled_for_territories: false,
    resource: "free_trial_developers",
    action: "edit",
  },
  {
    permission: permissions.manageCustomEventPropertySegmentation,
    display_name: t(
      "manage-custom-event-property-segmentation-display-name",
      "Manage Custom Event Property Segmentation"
    ),
    feature_flipper: [{ name: "event_property_segmentation_with_entitlement", match_value: true }],
    helper_text: t(
      "helper-text-manage-custom-event-property-segmentation",
      "Allows users to manage custom event and purchase properties for segmentation"
    ),
    definition: null,
    enabled_for_territories: false,
    resource: "custom_event_property_segmentation",
    action: "edit",
  },
  // Deprecated
  {
    permission: permissions.exportDashboardUsers,
    display_name: t("export-dashboard-users-display-name", "Export Dashboard Users"),
    feature_flipper: [{ name: "granular_export_dashboard_users_permission", match_value: true }],
    helper_text: t("helper-text-export-dashboard-users", "Allows developers to export dashboard users"),
    definition: null,
    enabled_for_territories: false,
    resource: "dashboard_users",
    action: "export",
  },
  {
    permission: permissions.viewTeams,
    display_name: t("view-teams-display-name", "View Teams"),
    definition: t("view-teams-definition", "View Teams without making changes."),
    feature_flipper: [
      { name: "territories", match_value: true },
      { name: "granular_team_management_permissions", match_value: true },
      { name: "clip_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-view-teams", "Allows developers to view workspace teams"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageTerritories,
    resource: "teams",
    action: "view",
  },
  {
    permission: permissions.editLandingPageTemplates,
    display_name: t("edit-landing-page-templates-display-name", "Edit Landing Page Templates"),
    feature_flipper: null,
    helper_text: t(
      "helper-text-edit-landing-page-templates",
      "Allows users to create and edit Landing Page Templates"
    ),
    definition: t(
      "edit-landing-page-templates-definition",
      "Allows users to create and edit landing page templates."
    ),
    enabled_for_territories: false,
    resource: "landing_page_templates",
    action: "edit",
  },
  {
    permission: permissions.viewLandingPageTemplates,
    display_name: t("view-landing-page-templates-display-name", "View Landing Page Templates"),
    feature_flipper: null,
    helper_text: t("helper-text-view-landing-page-templates", "Allows users to view Landing Page Templates"),
    definition: t("view-landing-page-templates-definition", "Allows users to view landing page templates."),
    enabled_for_territories: false,
    resource: "landing_page_templates",
    action: "view",
  },
  {
    permission: permissions.archiveLandingPageTemplates,
    display_name: t("archive-landing-page-templates-display-name", "Archive Landing Page Templates"),
    feature_flipper: null,
    helper_text: t("helper-text-archive-landing-page-templates", "Allows users to archive Landing Page Templates"),
    definition: t("archive-landing-page-templates-definition", "Allows users to archive landing page templates."),
    enabled_for_territories: false,
    resource: "landing_page_templates",
    action: "archive",
  },
  {
    permission: permissions.editDomainSettings,
    display_name: t("edit-domain-settings-templates-display-name", "Edit Domain Settings"),
    feature_flipper: null,
    helper_text: t("helper-text-edit-domain-settings", "Allows users to manage Domains"),
    definition: null,
    enabled_for_territories: false,
    action: "edit",
  },
  {
    permission: permissions.editEmailSettings,
    display_name: t("edit-email-settings-display-name", "Edit Email Settings"),
    definition: t("edit-email-settings-definition", "Enable and update Email Preferences."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "email_granular_permission_manage_email_settings", match_value: true },
    ],
    helper_text: t("helper-text-edit-email-settings", "Allows users to edit Email Settings"),
    enabled_for_territories: false,
    resource: "email_settings",
    granular_permission_of: permissions.manageEmailSettings,
    action: "edit",
  },
  {
    permission: permissions.viewEmailSettings,
    display_name: t("view-email-settings-display-name", "View Email Settings"),
    definition: t("view-email-settings-definition", "View Email Preferences without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "email_granular_permission_manage_email_settings", match_value: true },
    ],
    helper_text: t("helper-text-view-email-settings", "Allows users to view email settings"),
    enabled_for_territories: false,
    resource: "email_settings",
    granular_permission_of: permissions.manageEmailSettings,
    action: "view",
  },
  {
    permission: permissions.deleteUsers,
    display_name: t("delete-users-display-name", "Delete Users"),
    definition: t(
      "delete-users-definition",
      "Permanently delete users from the dashboard individually or in bulk."
    ),
    feature_flipper: [{ name: "self_service_user_deletion", match_value: true }],
    helper_text: t("helper-text-delete-users", "Allows dashboard users to delete users"),
    enabled_for_territories: false,
    resource: "user_data",
    action: "delete",
  },
  {
    permission: permissions.viewFeatureFlags,
    display_name: t("view-feature-flags-display-name", "View Feature Flags"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "feature_flags_permissions_management", match_value: true },
    ],
    helper_text: t("helper-text-view-feature-flags", "Allows users to view feature flags"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "feature_flags",
    action: "view",
    definition: t("view-feature-flags-definition", "View feature flags without making changes."),
  },
  {
    permission: permissions.editFeatureFlags,
    display_name: t("edit-feature-flags-display-name", "Edit Feature Flags"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "feature_flags_permissions_management", match_value: true },
    ],
    helper_text: t("helper-text-edit-feature-flags", "Allows users to create and edit feature flags"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageFeatureFlags,
    resource: "feature_flags",
    action: "edit",
    definition: t("edit-feature-flags-definition", "Create and update feature flags."),
  },
  {
    permission: permissions.archiveFeatureFlags,
    display_name: t("archive-feature-flags-display-name", "Archive Feature Flags"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "feature_flags_permissions_management", match_value: true },
    ],
    helper_text: t("helper-text-archive-feature-flags", "Allows users to archive feature flags"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageFeatureFlags,
    resource: "feature_flags",
    action: "archive",
    definition: t("archive-feature-flags-definition", "Move feature flags to archive."),
  },
  {
    permission: permissions.viewPlacements,
    display_name: t("view-placements-display-name", "View Placements"),
    feature_flipper: [
      { name: "banners", match_value: true },
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "content_cards_permissions_placements", match_value: true },
    ],
    helper_text: t(
      "helper-text-view-placements",
      "Allows users to view Placements, but not modify or archive them"
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "placements",
    action: "view",
    definition: t("view-placements-definition", "View Banner placements without making changes."),
  },
  {
    permission: permissions.editPlacements,
    display_name: t("edit-placements-display-name", "Edit Placements"),
    feature_flipper: [
      { name: "banners", match_value: true },
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "content_cards_permissions_placements", match_value: true },
    ],
    helper_text: t(
      "helper-text-edit-placements",
      "Allows users to create and edit Placements, but not archive them"
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageApps,
    resource: "placements",
    action: "edit",
    definition: t("edit-placements-definition", "Create and update Banner placements."),
  },
  {
    permission: permissions.archivePlacements,
    display_name: t("archive-placements-display-name", "Archive Placements"),
    feature_flipper: [
      { name: "banners", match_value: true },
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "content_cards_permissions_placements", match_value: true },
    ],
    helper_text: t("helper-text-archive-placements", "Allows users to archive and unarchive Placements"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageApps,
    resource: "placements",
    action: "archive",
    definition: t("archive-placements-definition", "Move Banner placements to archive."),
  },
  {
    permission: permissions.viewUserDeletionRecords,
    display_name: t("view-user-deletion-records-display-name", "View User Deletion Records"),
    definition: t("view-user-deletion-records-definition", "View user deletion records."),
    feature_flipper: [{ name: "self_service_user_deletion", match_value: true }],
    helper_text: t("helper-text-view-delete-users", "Allows dashboard users to view the user deletion table"),
    enabled_for_territories: false,
    resource: "user_deletion_records",
    action: "view",
  },
  {
    permission: permissions.viewCustomAttributes,
    display_name: t("view-custom-attributes-display-name", "View Custom Attributes"),
    definition: t("view-custom-attributes-definition", "View custom attributes without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-view-custom-attributes", "Allows users to view custom attributes"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "custom_attributes",
    action: "view",
  },
  {
    permission: permissions.editCustomAttributes,
    display_name: t("edit-custom-attributes-display-name", "Edit Custom Attributes"),
    definition: t("edit-custom-attributes-definition", "Create and update custom attributes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-edit-custom-attributes", "Allows users to create and edit custom attributes"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_attributes",
    action: "edit",
  },
  {
    permission: permissions.blocklistCustomAttributes,
    display_name: t("blocklist-custom-attributes-display-name", "Blocklist Custom Attributes"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-blocklist-custom-attributes", "Allows users to blocklist custom attributes"),
    definition: t(
      "blocklist-custom-attributes-definition",
      "Add custom attributes to a blocklist that restricts use in the dashboard."
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_attributes",
    action: "blocklist",
  },
  {
    permission: permissions.deleteCustomAttributes,
    display_name: t("delete-custom-attributes-display-name", "Delete Custom Attributes"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-delete-custom-attributes", "Allows users to delete custom attributes"),
    definition: t("delete-custom-attributes-definition", "Permanently delete custom attributes."),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_attributes",
    action: "delete",
  },
  {
    permission: permissions.exportCustomAttributes,
    display_name: t("export-custom-attributes-display-name", "Export Custom Attributes"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-export-custom-attributes", "Allows users to export custom attributes"),
    definition: t("export-custom-attributes-definition", "Download custom attributes from the dashboard."),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_attributes",
    action: "export",
  },
  {
    permission: permissions.viewCustomEvents,
    display_name: t("view-custom-events-display-name", "View Custom Events"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-view-custom-events", "Allows users to view custom events"),
    definition: t("view-custom-events-definition", "View custom events without making changes."),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "custom_events",
    action: "view",
  },
  {
    permission: permissions.editCustomEvents,
    display_name: t("edit-custom-events-display-name", "Edit Custom Events"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-edit-custom-events", "Allows users to create and edit custom events"),
    definition: t("edit-custom-events-definition", "Create and update custom events."),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_events",
    action: "edit",
  },
  {
    permission: permissions.blocklistCustomEvents,
    display_name: t("blocklist-custom-events-display-name", "Blocklist Custom Events"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-blocklist-custom-events", "Allows users to blocklist custom events"),
    definition: t(
      "blocklist-custom-events-definition",
      "Add custom events to a blocklist that restricts use in the dashboard."
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_events",
    action: "blocklist",
  },
  {
    permission: permissions.deleteCustomEvents,
    display_name: t("delete-custom-events-display-name", "Delete Custom Events"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-delete-custom-events", "Allows users to delete custom events"),
    definition: t("delete-custom-events-definition", "Permanently delete custom events."),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_events",
    action: "delete",
  },
  {
    permission: permissions.exportCustomEvents,
    display_name: t("export-custom-events-display-name", "Export Custom Events"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-export-custom-events", "Allows users to export custom events"),
    definition: t("export-custom-events-definition", "Download custom events from the dashboard."),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_events",
    action: "export",
  },
  {
    permission: permissions.editCustomEventPropertySegmentation,
    display_name: t(
      "edit-custom-event-property-segmentation-display-name",
      "Edit Custom Event Property Segmentation"
    ),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t(
      "helper-text-edit-custom-event-property-segmentation",
      "Allows users to edit custom event property segmentation"
    ),
    definition: t(
      "edit-custom-event-property-segmentation-definition",
      "Enable segmentation for custom event properties."
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "custom_event_property_segmentation",
    action: "edit",
  },
  {
    permission: permissions.viewProducts,
    display_name: t("view-products-display-name", "View Products"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-view-products", "Allows users to view products"),
    definition: t("view-products-definition", "View products without making changes."),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "products",
    action: "view",
  },
  {
    permission: permissions.editProducts,
    display_name: t("edit-products-display-name", "Edit Products"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-edit-products", "Allows users to create and edit products"),
    definition: t("edit-products-definition", "Create and update products."),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "products",
    action: "edit",
  },
  {
    permission: permissions.blocklistProducts,
    display_name: t("blocklist-products-display-name", "Blocklist Products"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t("helper-text-blocklist-products", "Allows users to blocklist products"),
    definition: t(
      "blocklist-products-definition",
      "Add products to a blocklist that restricts use in the dashboard."
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "products",
    action: "blocklist",
  },
  {
    permission: permissions.editPurchasePropertySegmentation,
    display_name: t("edit-purchase-property-segmentation-display-name", "Edit Purchase Property Segmentation"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_custom_attributes_events_products", match_value: true },
    ],
    helper_text: t(
      "helper-text-edit-purchase-property-segmentation",
      "Allows users to edit purchase property segmentation"
    ),
    definition: t("edit-purchase-property-segmentation-definition", "Enable segmentation for purchase properties."),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCustomAttributesEventsProducts,
    resource: "purchase_property_segmentation",
    action: "edit",
  },
  {
    permission: permissions.viewGlobalRateLimits,
    display_name: t("view-global-rate-limits-display-name", "View Messaging Rate Limits"),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-view-global-rate-limits", "Allows users to view messaging rate limits"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "global_rate_limits",
    action: "view",
  },
  {
    permission: permissions.editGlobalRateLimits,
    display_name: t("edit-global-rate-limits-display-name", "Edit Messaging Rate Limits"),
    feature_flipper: null,
    helper_text: t("helper-text-edit-global-rate-limits", "Allows users to edit messaging rate limits"),
    definition: null,
    enabled_for_territories: false,
    resource: "global_rate_limits",
    action: "edit",
  },
  {
    permission: permissions.viewBannerTemplates,
    display_name: t("view-banner-templates-display-name", "View Banner Templates"),
    definition: t("view-banner-templates-definition", "View Banner templates."),
    feature_flipper: [{ name: "banners_template_library", match_value: true }],
    helper_text: t(
      "helper-text-view-banner-templates",
      "Allows users to view Banner Templates, but not modify or archive them"
    ),
    enabled_for_territories: false,
    resource: "banner_templates",
    action: "view",
  },
  {
    permission: permissions.editBannerTemplates,
    display_name: t("edit-banner-templates-display-name", "Edit Banner Templates"),
    definition: t("edit-banner-templates-definition", "Create and edit Banner templates."),
    feature_flipper: [{ name: "banners_template_library", match_value: true }],
    helper_text: t(
      "helper-text-edit-banner-templates",
      "Allows users to create and edit Banner Templates, but not archive them"
    ),
    enabled_for_territories: false,
    resource: "banner_templates",
    action: "edit",
  },
  {
    permission: permissions.archiveBannerTemplates,
    display_name: t("archive-banner-templates-display-name", "Archive Banner Templates"),
    definition: t("archive-banner-templates-definition", "Archive Banner templates."),
    feature_flipper: [{ name: "banners_template_library", match_value: true }],
    helper_text: t(
      "helper-text-archive-banner-templates",
      "Allows users to archive and unarchive Banner Templates"
    ),
    enabled_for_territories: false,
    resource: "banner_templates",
    action: "archive",
  },
  {
    permission: permissions.editDashboardUsers,
    display_name: t("edit-dashboard-users-display-name", "Edit Dashboard Users"),
    definition: t("edit-dashboard-users-definition", "View, create, and edit company users."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_manage_developers_and_permissions", match_value: true },
    ],
    helper_text: t("helper-text-edit-dashboard-users", "Allows users to edit dashboard users"),
    sensitive_tooltip_text: t("edit-dashboard-users-sensitive-tooltip-text", "Gives control over other users"),
    enabled_for_territories: true,
    granular_permission_of: permissions.manageDevelopersAndPermissions,
    resource: "dashboard_users",
    action: "edit",
  },
  {
    permission: permissions.viewCatalogs,
    display_name: t("view-catalogs-display-name", "View Catalogs"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_catalogs_dashboard_permission", match_value: true },
    ],
    helper_text: t("helper-text-view-catalogs", "Allows users to view catalogs and catalog selections"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCatalogsDashboardPermission,
    resource: "catalogs",
    action: "view",
    definition: t("view-catalogs-definition", "View catalogs without making changes."),
  },
  {
    permission: permissions.editCatalogs,
    display_name: t("edit-catalogs-display-name", "Edit Catalogs"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_catalogs_dashboard_permission", match_value: true },
    ],
    helper_text: t("helper-text-edit-catalogs", "Allows users to create and edit catalogs and catalog selections"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCatalogsDashboardPermission,
    resource: "catalogs",
    action: "edit",
    definition: t("edit-catalogs-definition", "Create and update catalogs."),
  },
  {
    permission: permissions.exportCatalogs,
    display_name: t("export-catalogs-display-name", "Export Catalogs"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_catalogs_dashboard_permission", match_value: true },
    ],
    helper_text: t("helper-text-export-catalogs", "Allows users to export catalogs"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCatalogsDashboardPermission,
    resource: "catalogs",
    action: "export",
    definition: t("export-catalogs-definition", "Download catalogs from the dashboard."),
  },
  {
    permission: permissions.deleteCatalogs,
    display_name: t("delete-catalogs-display-name", "Delete Catalogs"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "co_permissions_manage_catalogs_dashboard_permission", match_value: true },
    ],
    helper_text: t("helper-text-delete-catalogs", "Allows users to delete catalogs and catalog selections"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageCatalogsDashboardPermission,
    resource: "catalogs",
    action: "delete",
    definition: t("delete-catalogs-definition", "Permanently delete catalogs."),
  },
  {
    permission: permissions.viewLocations,
    display_name: t("view-locations-display-name", "View Locations"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "in_app_messages_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-locations", "Allows users to view locations"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "locations",
    action: "view",
    definition: t("view-locations-definition", "View locations without making changes."),
  },
  {
    permission: permissions.editLocations,
    display_name: t("edit-locations-display-name", "Edit Locations"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "in_app_messages_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-edit-locations", "Allows users to edit locations"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "locations",
    action: "edit",
    definition: t("edit-locations-definition", "Create and edit locations."),
  },
  {
    permission: permissions.archiveLocations,
    display_name: t("archive-locations-display-name", "Archive Locations"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "in_app_messages_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-archive-locations", "Allows users to archive locations"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "locations",
    action: "archive",
    definition: t("archive-locations-definition", "Move locations to archive."),
  },
  {
    permission: permissions.viewCustomAgents,
    display_name: t("view-custom-agents", "View Custom AI Agents"),
    definition: t("view-custom-agents-definition", "Allows users to view custom AI agents."),
    feature_flipper: [{ name: "custom_ai_agents", match_value: true }],
    helper_text: t("helper-text-view-custom-agents", "Allows users to view custom AI agents"),
    enabled_for_territories: true,
    resource: "custom_agents",
    action: "view",
  },
  {
    permission: permissions.editCustomAgents,
    display_name: t("edit-custom-agents", "Create/Edit Custom AI Agents"),
    definition: t("edit-custom-agents-definition", "Allows users to create and edit custom AI agents."),
    feature_flipper: [{ name: "custom_ai_agents", match_value: true }],
    helper_text: t("helper-text-edit-custom-agents", "Allows users to create and edit custom AI agents"),
    enabled_for_territories: true,
    resource: "custom_agents",
    action: "edit",
  },
  {
    permission: permissions.archiveCustomAgents,
    display_name: t("archive-custom-agents", "Archive Custom AI Agents"),
    definition: t("archive-custom-agents-definition", "Allows users to archive custom AI agents."),
    feature_flipper: [{ name: "custom_ai_agents", match_value: true }],
    helper_text: t("helper-text-archive-custom-agents", "Allows users to archive custom AI agents"),
    enabled_for_territories: true,
    resource: "custom_agents",
    action: "archive",
  },
  {
    permission: permissions.viewUserMergeRecords,
    display_name: t("view-user-merge-records-display-name", "View User Merge Records"),
    definition: t("view-user-merge-records-definition", "View merge users."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-view-user-merge-records", "Allows users to view user merge records"),
    enabled_for_territories: false,
    granular_permission_of: permissions.mergeDuplicateUsers,
    resource: "user_merge_records",
    action: "view",
  },
  {
    permission: permissions.viewCrossChannelProfileLinking,
    display_name: t("view-cross-channel-profile-linking-display-name", "View Cross Channel Profile Linking"),
    feature_flipper: [{ name: "sdk_profile_stitching", match_value: true }],
    helper_text: t(
      "helper-text-view-cross-channel-profile-linking",
      "Allows users to view cross channel profile linking"
    ),
    definition: null,
    enabled_for_territories: false,
    resource: "cross_channel_profile_linking",
    action: "view",
  },
  {
    permission: permissions.editCrossChannelProfileLinking,
    display_name: t("edit-cross-channel-profile-linking-display-name", "Edit Cross Channel Profile Linking"),
    feature_flipper: [{ name: "sdk_profile_stitching", match_value: true }],
    helper_text: t(
      "helper-text-edit-cross-channel-profile-linking",
      "Allows users to edit cross channel profile linking"
    ),
    definition: null,
    enabled_for_territories: false,
    resource: "cross_channel_profile_linking",
    action: "edit",
  },
  {
    permission: permissions.viewApiKeys,
    display_name: t("view-api-keys-display-name", "View API Keys"),
    definition: t("view-api-keys-definition", "View API keys without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_developer_console", match_value: true },
    ],
    helper_text: t("helper-text-view-api-keys", "Allows users to view API keys"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "api_keys",
    action: "view",
  },
  {
    permission: permissions.editApiKeys,
    display_name: t("edit-api-keys-display-name", "Edit API Keys"),
    definition: t("edit-api-keys-definition", "Create and update API keys."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_developer_console", match_value: true },
    ],
    helper_text: t("helper-text-edit-api-keys", "Allows users to edit API keys"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "api_keys",
    action: "edit",
  },
  {
    permission: permissions.viewApiIdentifiers,
    display_name: t("view-api-identifiers-display-name", "View API Identifiers"),
    definition: t("view-api-identifiers-definition", "View API identifiers and other identifiers."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_developer_console", match_value: true },
    ],
    helper_text: t("helper-text-view-api-identifiers", "Allows users to view API identifiers"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "api_identifiers",
    action: "view",
  },
  {
    permission: permissions.viewEventUserLog,
    display_name: t("view-event-user-log-display-name", "View Event User Log"),
    definition: t("view-event-user-log-definition", "View event user logs."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_developer_console", match_value: true },
    ],
    helper_text: t("helper-text-view-event-user-log", "Allows users to view event user log"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "event_user_log",
    action: "view",
  },
  {
    permission: permissions.viewApiUsageAlerts,
    display_name: t("view-api-usage-alerts-display-name", "View API Usage Alerts"),
    definition: t("view-api-usage-alerts-definition", "View API usage alerts without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_developer_console", match_value: true },
    ],
    helper_text: t("helper-text-view-api-usage-alerts", "Allows users to view API usage alerts"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "api_usage_alerts",
    action: "view",
  },
  {
    permission: permissions.editApiUsageAlerts,
    display_name: t("edit-api-usage-alerts-display-name", "Edit API Usage Alerts"),
    definition: t("edit-api-usage-alerts-definition", "Create and update API usage alerts."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_developer_console", match_value: true },
    ],
    helper_text: t("helper-text-edit-api-usage-alerts", "Allows users to edit API usage alerts"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "api_usage_alerts",
    action: "edit",
  },
  {
    permission: permissions.viewApiUsageDashboard,
    display_name: t("view-api-usage-dashboard-display-name", "View API Usage Dashboard"),
    definition: t("view-api-usage-dashboard-definition", "View the API usage dashboard."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_developer_console", match_value: true },
    ],
    helper_text: t("helper-text-view-api-usage-dashboard", "Allows users to view API usage dashboard"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "api_usage_data",
    action: "view",
  },
  {
    permission: permissions.viewApiLimits,
    display_name: t("view-api-limits-display-name", "View API Limits"),
    definition: t("view-api-limits-definition", "View API rate limits."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_developer_console", match_value: true },
      { name: "api_limits", match_value: true },
    ],
    helper_text: t("helper-text-view-api-limits", "Allows users to view API limits"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "api_limits",
    action: "view",
  },
  {
    permission: permissions.viewImportUsers,
    display_name: t("view-import-users-display-name", "View Import Users"),
    definition: t("view-import-users-definition", "View CSV user imports without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_import_and_update_users", match_value: true },
    ],
    helper_text: t("helper-text-view-import-users", "Allows users to view import users"),
    enabled_for_territories: false,
    granular_permission_of: permissions.importAndUpdateUsers,
    resource: "import_users",
    action: "view",
  },
  {
    permission: permissions.editUserData,
    display_name: t("edit-user-data-display-name", "Edit User Data"),
    definition: t("edit-user-data-definition", "Create and update user data."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_import_and_update_users", match_value: true },
    ],
    helper_text: t("helper-text-edit-user-data", "Allows users to edit user data"),
    enabled_for_territories: false,
    granular_permission_of: permissions.importAndUpdateUsers,
    resource: "user_data",
    action: "edit",
  },
  {
    permission: permissions.viewPushSettings,
    display_name: t("view-push-settings-display-name", "View Push Settings"),
    definition: t("view-push-settings-definition", "View Push settings without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "push_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-push-settings", "Allows users to view push settings"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "push_settings",
    action: "view",
  },
  {
    permission: permissions.editPushSettings,
    display_name: t("edit-push-settings-display-name", "Edit Push Settings"),
    definition: t("edit-push-settings-definition", "Create and update Push settings."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "push_granular_permission_manage_apps", match_value: true },
    ],
    helper_text: t("helper-text-edit-push-settings", "Allows users to edit push settings"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageApps,
    resource: "push_settings",
    action: "edit",
  },
  {
    permission: permissions.viewMessageActivityLog,
    display_name: t("view-message-activity-log-display-name", "View Message Activity Log"),
    definition: t("view-message-activity-log-definition", "View message activity logs."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-view-message-activity-log", "Allows users to view message activity log"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "message_activity_log",
    action: "view",
  },
  {
    permission: permissions.viewContentBlocks,
    display_name: t("view-content-blocks-display-name", "View Content Blocks"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_content_blocks", match_value: true },
    ],
    helper_text: t("helper-text-view-content-blocks", "Allows users to view Content Blocks"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "content_blocks",
    action: "view",
    definition: t("view-content-blocks-definition", "View Content Blocks without making changes."),
  },
  {
    permission: permissions.editContentBlocks,
    display_name: t("edit-content-blocks-display-name", "Edit Content Blocks"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_content_blocks", match_value: true },
    ],
    helper_text: t("helper-text-edit-content-blocks", "Allows users to edit Content Blocks"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "content_blocks",
    action: "edit",
    definition: t("edit-content-blocks-definition", "Create new Content Blocks and edit draft Content Blocks."),
  },
  {
    permission: permissions.archiveContentBlocks,
    display_name: t("archive-content-blocks-display-name", "Archive Content Blocks"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_content_blocks", match_value: true },
    ],
    helper_text: t("helper-text-archive-content-blocks", "Allows users to archive Content Blocks"),
    enabled_for_territories: true,
    granular_permission_of: permissions.launchAndManageContentBlocks,
    resource: "content_blocks",
    action: "archive",
    definition: t("archive-content-blocks-definition", "Move Content Blocks to archive."),
  },
  {
    permission: permissions.launchContentBlocks,
    display_name: t("launch-content-blocks-display-name", "Launch Content Blocks"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_content_blocks", match_value: true },
    ],
    helper_text: t("helper-text-launch-content-blocks", "Allows users to launch Content Blocks"),
    enabled_for_territories: true,
    granular_permission_of: permissions.launchAndManageContentBlocks,
    resource: "content_blocks",
    action: "launch",
    definition: t(
      "launch-content-blocks-definition",
      "Publish draft Content Blocks and edit, archive, and unarchive launched Content Blocks."
    ),
  },
  {
    permission: permissions.viewCampaigns,
    display_name: t("view-campaigns-display-name", "View Campaigns"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-view-campaigns", "Allows users to view campaigns"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "campaigns",
    action: "view",
    definition: t("view-campaigns-definition", "View campaigns without making changes."),
  },
  {
    permission: permissions.editCampaigns,
    display_name: t("edit-campaigns-display-name", "Edit Campaigns"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-edit-campaigns", "Allows users to edit campaigns"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "campaigns",
    action: "edit",
    definition: t("edit-campaigns-definition", "Create and update campaigns."),
  },
  {
    permission: permissions.archiveCampaigns,
    display_name: t("archive-campaigns-display-name", "Archive Campaigns"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-archive-campaigns", "Allows users to archive campaigns"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "campaigns",
    action: "archive",
    definition: t("archive-campaigns-definition", "Move campaigns to archive."),
  },
  {
    permission: permissions.viewCanvases,
    display_name: t("view-canvases-display-name", "View Canvases"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-view-canvases", "Allows users to view canvases"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "canvas",
    action: "view",
    definition: t("view-canvases-definition", "View Canvases without making changes."),
  },
  {
    permission: permissions.editCanvases,
    display_name: t("edit-canvases-display-name", "Edit Canvases"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-edit-canvases", "Allows users to edit canvases"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "canvas",
    action: "edit",
    definition: t("edit-canvases-definition", "Create and update Canvases."),
  },
  {
    permission: permissions.archiveCanvases,
    display_name: t("archive-canvases-display-name", "Archive Canvases"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-archive-canvases", "Allows users to archive canvases"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "canvas",
    action: "archive",
    definition: t("archive-canvases-definition", "Move Canvases to archive."),
  },
  {
    permission: permissions.launchCampaigns,
    display_name: t("launch-campaigns-display-name", "Launch Campaigns"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-launch-campaigns", "Allows users to launch campaigns"),
    sensitive_tooltip_text: t("send-campaigns-sensitive-tooltip-text", "Potential for accidental sends"),
    enabled_for_territories: true,
    granular_permission_of: permissions.sendCampaigns,
    resource: "campaigns",
    action: "launch",
    definition: t("launch-campaigns-definition", "Start, stop, pause, or resume campaigns."),
  },
  {
    permission: permissions.launchCanvases,
    display_name: t("launch-canvases-display-name", "Launch Canvases"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-launch-canvases", "Allows users to launch canvases"),
    sensitive_tooltip_text: t("send-canvases-sensitive-tooltip-text", "Potential for accidental sends"),
    enabled_for_territories: true,
    granular_permission_of: permissions.sendCampaigns,
    resource: "canvas",
    action: "launch",
    definition: t("launch-canvases-definition", "Start, stop, pause, or resume Canvases."),
  },
  {
    permission: permissions.editCloudDataIngestion,
    display_name: t("edit-cloud-data-ingestion-display-name", "Edit Cloud Data Ingestion"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "cdit_granular_permission_external_integrations", match_value: true },
    ],
    helper_text: t("helper-text-edit-cloud-data-ingestion", "Allows users to edit cloud data ingestion"),
    enabled_for_territories: false,
    granular_permission_of: permissions.externalIntegrations,
    resource: "cloud_data_ingestion",
    action: "edit",
    definition: t("edit-cloud-data-ingestion-definition", "Create, update, and delete sources and syncs."),
  },
  {
    permission: permissions.viewVouchers,
    display_name: t("view-vouchers-display-name", "View Promotion Codes"),
    definition: t("view-vouchers-definition", "View promotion codes without making changes."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-view-vouchers", "Allows users to view promotion codes"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "promotion_codes",
    action: "view",
  },
  {
    permission: permissions.editVouchers,
    display_name: t("edit-vouchers-display-name", "Edit Promotion Codes"),
    definition: t("edit-vouchers-definition", "Create and update promotion codes."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-edit-vouchers", "Allows users to edit promotion codes"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "promotion_codes",
    action: "edit",
  },
  {
    permission: permissions.exportVouchers,
    display_name: t("export-vouchers-display-name", "Export Promotion Codes"),
    definition: t("export-vouchers-definition", "Download a list of promotion codes from the dashboard."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-export-vouchers", "Allows users to export promotion codes"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "promotion_codes",
    action: "export",
  },
  {
    permission: permissions.importUsers,
    display_name: t("import-users-display-name", "Import Users"),
    definition: t("import-users-definition", "Upload users to the dashboard."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "ingestion_granular_permission_import_and_update_users", match_value: true },
    ],
    helper_text: t("helper-text-import-users", "Allows users to import users"),
    enabled_for_territories: false,
    granular_permission_of: permissions.importAndUpdateUsers,
    resource: "import_users",
    action: "import",
  },
  {
    permission: permissions.viewPreferenceCenters,
    display_name: t("view-preference-centers-display-name", "View Preference Centers"),
    definition: t("view-preference-centers-definition", "View preference centers without making changes."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-view-preference-centers", "Allows users to view preference centers"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "preference_centers",
    action: "view",
  },
  {
    permission: permissions.editPreferenceCenters,
    display_name: t("edit-preference-centers-display-name", "Edit Preference Centers"),
    definition: t("edit-preference-centers-definition", "Create and update preference centers."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-edit-preference-centers", "Allows users to edit preference centers"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "preference_centers",
    action: "edit",
  },
  {
    permission: permissions.viewMessagePrioritization,
    display_name: t("view-message-prioritization-display-name", "View Message Prioritization"),
    definition: t(
      "view-message-prioritization-definition",
      "View Message Prioritization settings without making changes."
    ),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-view-message-prioritization", "Allows users to view message prioritization"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "message_prioritization",
    action: "view",
  },
  {
    permission: permissions.editMessagePrioritization,
    display_name: t("edit-message-prioritization-display-name", "Edit Message Prioritization"),
    definition: t("edit-message-prioritization-definition", "Create and update Message Prioritization settings."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-edit-message-prioritization", "Allows users to edit message prioritization"),
    enabled_for_territories: false,
    resource: "message_prioritization",
    action: "edit",
  },
  {
    permission: permissions.viewFrequencyCaps,
    display_name: t("view-frequency-caps-display-name", "View Frequency Capping Rules"),
    definition: t("view-frequency-caps-definition", "View Frequency Capping Rules without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-view-frequency-caps", "Allows users to view frequency capping rules"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "frequency_caps",
    action: "view",
  },
  {
    permission: permissions.editFrequencyCaps,
    display_name: t("edit-frequency-caps-display-name", "Edit Frequency Capping Rules"),
    definition: t("edit-frequency-caps-definition", "Create and update Frequency Capping Rules."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "mx_granular_permission_campaigns_canvases", match_value: true },
    ],
    helper_text: t("helper-text-edit-frequency-caps", "Allows users to edit frequency capping rules"),
    enabled_for_territories: false,
    granular_permission_of: permissions.sendCampaigns,
    resource: "frequency_caps",
    action: "edit",
  },
  {
    permission: permissions.viewMultiLanguageLocaleSettings,
    display_name: t("view-multi-language-locale-settings-display-name", "View Localization Settings"),
    definition: t("view-multi-language-locale-settings-definition", "View Multi Language locale settings page."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_multi_language", match_value: true },
    ],
    helper_text: t("helper-text-view-multi-language-locale-settings", "Allows users to view localization settings"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "multi_language_settings",
    action: "view",
  },
  {
    permission: permissions.editMultiLanguageLocaleSettings,
    display_name: t("edit-multi-language-locale-settings-display-name", "Edit Localization Settings"),
    definition: t("edit-multi-language-locale-settings-definition", "Create Multi Language locales."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_multi_language", match_value: true },
    ],
    helper_text: t("helper-text-edit-multi-language-locale-settings", "Allows users to edit localization settings"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageMultiLanguageSettings,
    resource: "multi_language_settings",
    action: "edit",
  },
  {
    permission: permissions.deleteMultiLanguageLocaleSettings,
    display_name: t("delete-multi-language-locale-settings-display-name", "Delete Localization Settings"),
    definition: t("delete-multi-language-locale-settings-definition", "Delete Multi Language locales."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_multi_language", match_value: true },
    ],
    helper_text: t(
      "helper-text-delete-multi-language-locale-settings",
      "Allows users to delete localization settings"
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageMultiLanguageSettings,
    resource: "multi_language_settings",
    action: "delete",
  },
  {
    permission: permissions.viewMediaLibraryAssets,
    display_name: t("view-media-library-assets-display-name", "View Media Library Assets"),
    definition: t("view-media-library-assets-definition", "View media library assets without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_media_library", match_value: true },
    ],
    helper_text: t("helper-text-view-media-library-assets", "Allows users to view media library assets"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "media_library_assets",
    action: "view",
  },
  {
    permission: permissions.editMediaLibraryAssets,
    display_name: t("edit-media-library-assets-display-name", "Edit Media Library Assets"),
    definition: t("edit-media-library-assets-definition", "Create and update media library assets."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_media_library", match_value: true },
    ],
    helper_text: t("helper-text-edit-media-library-assets", "Allows users to edit media library assets"),
    enabled_for_territories: true,
    granular_permission_of: permissions.manageMediaLibrary,
    resource: "media_library_assets",
    action: "edit",
  },
  {
    permission: permissions.deleteMediaLibraryAssets,
    display_name: t("delete-media-library-assets-display-name", "Delete Media Library Assets"),
    definition: t("delete-media-library-assets-definition", "Permanently delete media library assets."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "granular_permissions_media_library", match_value: true },
    ],
    helper_text: t("helper-text-delete-media-library-assets", "Allows users to delete media library assets"),
    enabled_for_territories: true,
    granular_permission_of: permissions.manageMediaLibrary,
    resource: "media_library_assets",
    action: "delete",
  },
  {
    permission: permissions.replaceMediaLibraryAssets,
    display_name: t("replace-media-library-assets-display-name", "Replace Media Library Assets"),
    definition: t(
      "replace-media-library-assets-definition",
      "Replace the underlying file of existing media library assets while preserving the URL."
    ),
    feature_flipper: [],
    helper_text: t(
      "helper-text-replace-media-library-assets",
      "Allows users to replace files in the media library"
    ),
    enabled_for_territories: true,
    resource: "media_library_assets",
    action: "replace",
  },
  {
    permission: permissions.viewInternalUserGroups,
    display_name: t("view-internal-user-groups-display-name", "View Internal Groups"),
    definition: t("view-internal-groups-definition", "View internal groups."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_internal_user_groups", match_value: true },
    ],
    helper_text: t("helper-text-view-internal-user-groups", "Allows users to view internal user groups"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "internal_groups",
    action: "view",
  },
  {
    permission: permissions.editInternalUserGroups,
    display_name: t("edit-internal-user-groups-display-name", "Edit Internal Groups"),
    definition: t("edit-internal-groups-definition", "Create and update internal groups."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_internal_user_groups", match_value: true },
    ],
    helper_text: t("helper-text-edit-internal-user-groups", "Allows users to create and edit internal user groups"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "internal_groups",
    action: "edit",
  },
  {
    permission: permissions.deleteInternalUserGroups,
    display_name: t("delete-internal-user-groups-display-name", "Delete Internal Groups"),
    definition: t("delete-internal-groups-definition", "Delete internal groups."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_internal_user_groups", match_value: true },
    ],
    helper_text: t("helper-text-delete-internal-user-groups", "Allows users to delete internal user groups"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "internal_groups",
    action: "delete",
  },
  {
    permission: permissions.viewSdkDebugger,
    display_name: t("view-sdk-debugger-display-name", "View SDK Debugger"),
    definition: t(
      "view-sdk-debugger-definition",
      "View SDK Debugger without making changes or debugging sessions."
    ),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-view-sdk-debugger", "Allows users to view sdk debugger"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "sdk_debugger",
    action: "view",
  },
  {
    permission: permissions.editSdkDebugger,
    display_name: t("edit-sdk-debugger-display-name", "Edit SDK Debugger"),
    definition: t("edit-sdk-debugger-definition", "Create and download SDK Debugger sessions."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-edit-sdk-debugger", "Allows users to edit sdk debugger"),
    enabled_for_territories: false,
    granular_permission_of: permissions.developerConsole,
    resource: "sdk_debugger",
    action: "edit",
  },
  {
    permission: permissions.editAppSettings,
    display_name: t("edit-app-settings-display-name", "Edit App Settings"),
    definition: t(
      "edit-app-settings-definition",
      "Add, delete, update apps, and manage their credentials and SDK authentication settings."
    ),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "feature_flags_granular_permission_manage_apps", match_value: true },
    ],
    helper_text: t("helper-text-edit-app-settings", "Allows users to edit app settings"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageApps,
    resource: "app_settings",
    action: "edit",
  },
  {
    permission: permissions.viewAppSettings,
    display_name: t("view-app-settings-display-name", "View App Settings"),
    definition: t("view-app-settings-definition", "View App Settings page without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "feature_flags_granular_permission_view_app_settings", match_value: true },
    ],
    helper_text: t("helper-text-view-app-settings", "Allows users to view app settings"),
    enabled_for_territories: false,
    resource: "app_settings",
    action: "view",
  },
  {
    permission: permissions.viewWhatsappMessageTemplates,
    display_name: t("view-whatsapp-message-templates-display-name", "View Whatsapp Message Templates"),
    feature_flipper: [{ name: "whats_app_template_builder", match_value: true }],
    helper_text: t(
      "helper-text-view-whatsapp-message-templates",
      "Allows users to view whatsapp message templates"
    ),
    definition: t(
      "view-whatsapp-message-templates-definition",
      "Allows users to view WhatsApp message templates created using Braze's WhatsApp Template Builder without making changes. This feature is currently in early access."
    ),
    enabled_for_territories: false,
    resource: "whatsapp_message_templates",
    action: "view",
  },
  {
    permission: permissions.editWhatsappMessageTemplates,
    display_name: t("edit-whatsapp-message-templates-display-name", "Edit Whatsapp Message Templates"),
    feature_flipper: [{ name: "whats_app_template_builder", match_value: true }],
    helper_text: t(
      "helper-text-edit-whatsapp-message-templates",
      "Allows users to create and edit whatsapp message templates"
    ),
    definition: t(
      "edit-whatsapp-message-templates-definition",
      "Allows users to create WhatsApp message templates using Braze's WhatsApp Template Builder. This feature is currently in early access."
    ),
    enabled_for_territories: false,
    resource: "whatsapp_message_templates",
    action: "edit",
  },
  {
    permission: permissions.deleteWhatsappMessageTemplates,
    display_name: t("delete-whatsapp-message-templates-display-name", "Delete Whatsapp Message Templates"),
    feature_flipper: [{ name: "whats_app_template_builder", match_value: true }],
    helper_text: t(
      "helper-text-delete-whatsapp-message-templates",
      "Allows users to delete whatsapp message templates"
    ),
    definition: t(
      "delete-whatsapp-message-templates-definition",
      "Allow users to delete any WhatsApp Message Template created within Braze's WhatsApp Template Builder. This feature is a work in progress and is not yet available for customers"
    ),
    enabled_for_territories: false,
    resource: "whatsapp_message_templates",
    action: "delete",
  },
  {
    permission: permissions.viewEmailTemplates,
    display_name: t("view-email-templates-display-name", "View Email Templates"),
    definition: t("view-email-templates-definition", "View email templates without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-email-templates", "Allows users to view email templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "email_templates",
    action: "view",
  },
  {
    permission: permissions.editEmailTemplates,
    display_name: t("edit-email-templates-display-name", "Edit Email Templates"),
    definition: t("edit-email-templates-definition", "Create and update email templates."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-edit-email-templates", "Allows users to edit email templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "email_templates",
    action: "edit",
  },
  {
    permission: permissions.archiveEmailTemplates,
    display_name: t("archive-email-templates-display-name", "Archive Email Templates"),
    definition: t("archive-email-templates-definition", "Move email templates to archive."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-archive-email-templates", "Allows users to archive email templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "email_templates",
    action: "archive",
  },
  {
    permission: permissions.viewIamTemplates,
    display_name: t("view-iam-templates-display-name", "View IAM Templates"),
    definition: t("view-iam-templates-definition", "View in-app message templates without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-iam-templates", "Allows users to view IAM templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "iam_templates",
    action: "view",
  },
  {
    permission: permissions.editIamTemplates,
    display_name: t("edit-iam-templates-display-name", "Edit IAM Templates"),
    definition: t("edit-iam-templates-definition", "Create and update in-app message templates."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-edit-iam-templates", "Allows users to edit IAM templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "iam_templates",
    action: "edit",
  },
  {
    permission: permissions.archiveIamTemplates,
    display_name: t("archive-iam-templates-display-name", "Archive IAM Templates"),
    definition: t("archive-iam-templates-definition", "Move IAM templates to archive."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-archive-iam-templates", "Allows users to archive IAM templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "iam_templates",
    action: "archive",
  },
  {
    permission: permissions.viewWebhookTemplates,
    display_name: t("view-webhook-templates-display-name", "View Webhook Templates"),
    definition: t("view-webhook-templates-definition", "View webhook templates without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-webhook-templates", "Allows users to view webhook templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "webhook_templates",
    action: "view",
  },
  {
    permission: permissions.editWebhookTemplates,
    display_name: t("edit-webhook-templates-display-name", "Edit Webhook Templates"),
    definition: t("edit-webhook-templates-definition", "Create and update webhook templates."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-edit-webhook-templates", "Allows users to edit webhook templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "webhook_templates",
    action: "edit",
  },
  {
    permission: permissions.archiveWebhookTemplates,
    display_name: t("archive-webhook-templates-display-name", "Archive Webhook Templates"),
    definition: t("archive-webhook-templates-definition", "Move webhook templates to archive."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-archive-webhook-templates", "Allows users to archive webhook templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "webhook_templates",
    action: "archive",
  },
  {
    permission: permissions.viewLinkTemplates,
    display_name: t("view-link-templates-display-name", "View Email Link Templates"),
    definition: t("view-link-templates-definition", "View link templates without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-link-templates", "Allows users to view email link templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "email_link_templates",
    action: "view",
  },
  {
    permission: permissions.editLinkTemplates,
    display_name: t("edit-link-templates-display-name", "Edit Email Link Templates"),
    definition: t("edit-link-templates-definition", "Create and update link templates."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-edit-link-templates", "Allows users to edit email link templates"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "email_link_templates",
    action: "edit",
  },
  {
    permission: permissions.createSupportTicket,
    display_name: t("create-support-ticket-display-name", "Edit Support Ticket"),
    feature_flipper: [{ name: "sage_ai_agentic_support_cases", match_value: true }],
    definition: t(
      "create-support-ticket-definition",
      "Create Support Ticket determines whether a user is allowed to call the endpoint that creates a support case; without it (when sage_ai_agentic is on), the create request is rejected by the authorization catalog."
    ),
    helper_text: t("helper-text-create-support-ticket", "Allows users to create support ticket"),
    enabled_for_territories: false,
    resource: "support_tickets",
    action: "edit",
  },
  {
    permission: permissions.useOperator,
    display_name: t("use-operator-display-name", "Use BrazeAI Operator™"),
    definition: t("use-operator-definition", "Allows users to use BrazeAI Operator."),
    feature_flipper: [
      { name: "sage_ai_agentic", match_value: true },
      { name: "fix_granular_permission_use_operator", match_value: true },
    ],
    helper_text: t("helper-text-use-operator", "Allows users to use BrazeAI Operator™"),
    enabled_for_territories: false,
    resource: "operator",
    action: "view",
  },
  {
    permission: permissions.editTeams,
    display_name: t("edit-teams-display-name", "Edit Teams"),
    definition: t("edit-teams-definition", "Create and update teams."),
    feature_flipper: [
      { name: "territories", match_value: true },
      { name: "granular_team_management_permissions", match_value: true },
      { name: "clip_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-edit-teams", "Allows developers to create and edit workspace teams"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageTerritories,
    resource: "teams",
    action: "edit",
  },
  {
    permission: permissions.archiveTeams,
    display_name: t("archive-teams-display-name", "Archive Teams"),
    definition: t("archive-teams-definition", "Move teams to archive."),
    feature_flipper: [
      { name: "territories", match_value: true },
      { name: "granular_team_management_permissions", match_value: true },
      { name: "clip_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-archive-teams", "Allows developers to archive workspace teams"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageTerritories,
    resource: "teams",
    action: "archive",
  },
  {
    permission: permissions.viewTags,
    display_name: t("view-tags-display-name", "View Tags"),
    definition: t("view-tags-definition", "View tags without making changes."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_manage_tags", match_value: true },
    ],
    helper_text: t("helper-text-view-tags", "Allows users to view tags"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageTags,
    resource: "tags",
    action: "view",
  },
  {
    permission: permissions.editTags,
    display_name: t("edit-tags-display-name", "Edit Tags"),
    definition: t("edit-tags-definition", "Create and update tags."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_manage_tags", match_value: true },
    ],
    helper_text: t("helper-text-edit-tags", "Allows users to edit tags"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageTags,
    resource: "tags",
    action: "edit",
  },
  {
    permission: permissions.deleteTags,
    display_name: t("delete-tags-display-name", "Delete Tags"),
    definition: t("delete-tags-definition", "Permanently delete tags."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "clip_granular_permission_manage_tags", match_value: true },
    ],
    helper_text: t("helper-text-delete-tags", "Allows users to delete tags"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageTags,
    resource: "tags",
    action: "delete",
  },
  {
    permission: permissions.viewDecisioningStudioAgents,
    display_name: t("view-decisioning-studio-agents-display-name", "View Decisioning Studio Agent"),
    definition: t(
      "view-decisioning-studio-agents-definition",
      "View Decisioning Studio Agents configuration without making changes."
    ),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "offerfit_customer_interface_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-decisioning-studio-agents", "Allows users to view decisioning studio agents"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "decisioning_studio_agents",
    action: "view",
  },
  {
    permission: permissions.viewDecisioningStudioConversionEvent,
    display_name: t("view-decisioning-studio-conversion-event-display-name", "View Conversion Event"),
    definition: t(
      "view-decisioning-studio-conversion-event-definition",
      "View Decisioning Studio Conversion Events without making changes."
    ),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "offerfit_customer_interface_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-decisioning-studio-conversion-event", "Allows users to view conversion event"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "decisioning_studio_conversion_event",
    action: "view",
  },
  {
    permission: permissions.viewGlobalControlGroup,
    display_name: t("view-global-control-group-display-name", "View Global Control Group"),
    definition: t("view-global-control-group-definition", "View Global Control Group setup page."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-view-global-control-group", "Allows users to view global control group"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "global_control_group",
    action: "view",
  },
  {
    permission: permissions.editGlobalControlGroup,
    display_name: t("edit-global-control-group-display-name", "Edit Global Control Group"),
    definition: t("edit-global-control-group-definition", "Edit and save Global Control Group."),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t("helper-text-edit-global-control-group", "Allows users to edit global control group"),
    enabled_for_territories: false,
    granular_permission_of: permissions.sendCampaigns,
    resource: "global_control_group",
    action: "edit",
  },
  {
    permission: permissions.viewDecisioningStudioAudience,
    display_name: t("view-decisioning-studio-audience-display-name", "View Decisioning Studio Audience"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "offerfit_customer_interface_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t(
      "helper-text-view-decisioning-studio-audience",
      "Allows users to view decisioning studio audience"
    ),
    definition: t(
      "view-decisioning-studio-audience-definition",
      "Allow users to see audience details on decisioning studio agent configuration summaries."
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "decisioning_studio_audience",
    action: "view",
  },
  {
    permission: permissions.viewReporting,
    display_name: t("view-reporting-display-name", "View Dashboard Reports"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "reporting_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-reporting", "Allows users to view dashboard reports"),
    definition: null,
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "dashboard_reports",
    action: "view",
  },
  {
    permission: permissions.editReporting,
    display_name: t("edit-reporting-display-name", "Edit Dashboard Reports"),
    definition: t("edit-reporting-definition", "Create and edit reports."),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "reporting_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-edit-reporting", "Allows users to edit dashboard reports"),
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "dashboard_reports",
    action: "edit",
  },
  {
    permission: permissions.deleteReporting,
    display_name: t("delete-reporting-display-name", "Delete Dashboard Reports"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "reporting_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-delete-reporting", "Allows users to delete dashboard reports"),
    definition: null,
    enabled_for_territories: true,
    granular_permission_of: permissions.basicReadOnly,
    resource: "dashboard_reports",
    action: "delete",
  },
  {
    permission: permissions.viewDecisioningStudioGuardrails,
    display_name: t("view-decisioning-studio-guardrails-display-name", "View Decisioning Studio Guardrails"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "offerfit_customer_interface_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t(
      "helper-text-view-decisioning-studio-guardrails",
      "Allows users to view decisioning studio guardrails"
    ),
    definition: t(
      "view-decisioning-studio-guardrails-definition",
      "Allow users to see configured guardrails on decisioning studio agent configuration summaries"
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "decisioning_studio_guardrails",
    action: "view",
  },
  {
    permission: permissions.viewWhatsappSettings,
    display_name: t("view-whatsapp-settings-display-name", "View WhatsApp Settings"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "whats_app_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-view-whatsapp-settings", "Allows users to view whatsapp settings"),
    definition: null,
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "whatsapp_settings",
    action: "view",
  },
  {
    permission: permissions.viewWhatsappFlows,
    display_name: t("view-whatsapp-flows-display-name", "View WhatsApp Flows"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "whats_app_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-view-whatsapp-flows", "Allows users to view whatsapp flows"),
    definition: null,
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "whatsapp_flows",
    action: "view",
  },
  {
    permission: permissions.viewWhatsappCatalog,
    display_name: t("view-whatsapp-catalog-display-name", "View WhatsApp Catalog"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "whats_app_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-view-whatsapp-catalog", "Allows users to view whatsapp catalog"),
    definition: null,
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "whatsapp_catalog",
    action: "view",
  },
  {
    permission: permissions.viewWhatsappMessageTemplatesFromMeta,
    display_name: t(
      "view-whatsapp-message-templates-from-meta-display-name",
      "View WhatsApp Message Templates From Meta"
    ),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "whats_app_granular_permissions_all", match_value: true },
    ],
    helper_text: t(
      "helper-text-view-whatsapp-message-templates-from-meta",
      "Allows users to view whatsapp message templates from meta"
    ),
    definition: null,
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "whatsapp_message_templates_from_meta",
    action: "view",
  },
  {
    permission: permissions.viewAudienceSyncSettings,
    display_name: t("view-audience-sync-settings-display-name", "View Audience Sync Settings"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "whats_app_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-view-audience-sync-settings", "Allows users to view audience sync settings"),
    definition: null,
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "audience_sync_settings",
    action: "view",
  },
  {
    permission: permissions.viewDomainSettings,
    display_name: t("view-domain-settings-display-name", "View Domain Settings"),
    feature_flipper: [{ name: "verified_domain", match_value: true }],
    helper_text: t("helper-text-view-domain-settings", "Allows users to view domain settings"),
    definition: null,
    enabled_for_territories: false,
    action: "view",
  },
  {
    permission: permissions.viewDecisioningStudioTiming,
    display_name: t("view-decisioning-studio-timing-display-name", "View Decisioning Studio Timing"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "offerfit_customer_interface_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-view-decisioning-studio-timing", "Allows users to view decisioning studio timing"),
    definition: t(
      "view-decisioning-studio-timing-definition",
      "View Decisioning Studio Timing configuration without making changes."
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "decisioning_studio_timing",
    action: "view",
  },
  {
    permission: permissions.viewDecisioningStudioActionBanks,
    display_name: t("view-decisioning-studio-action-banks-display-name", "View Decisioning Studio Action Banks"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "offerfit_customer_interface_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t(
      "helper-text-view-decisioning-studio-action-banks",
      "Allows users to view decisioning studio action banks"
    ),
    definition: t(
      "view-decisioning-studio-action-banks-definition",
      "View Decisioning Studio Action Banks without making changes."
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "decisioning_studio_action_banks",
    action: "view",
  },
  {
    permission: permissions.viewMessageArchivingSettings,
    display_name: t("view-message-archiving-settings-display-name", "View Message Archiving Settings"),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t(
      "helper-text-view-message-archiving-settings",
      "Allows users to view message archiving settings"
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageApps,
    resource: "message_archiving_settings",
    action: "view",
  },
  {
    permission: permissions.editMessageArchivingSettings,
    display_name: t("edit-message-archiving-settings-display-name", "Edit Message Archiving Settings"),
    feature_flipper: [{ name: "clip_granular_permissions_all", match_value: true }],
    helper_text: t(
      "helper-text-edit-message-archiving-settings",
      "Allows users to edit message archiving settings"
    ),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageApps,
    resource: "message_archiving_settings",
    action: "edit",
  },
  {
    permission: permissions.viewQuietHoursSettings,
    display_name: t("view-quiet-hours-settings-display-name", "View Quiet Hours Settings"),
    feature_flipper: [
      { name: "workspace_quiet_hours", match_value: true },
      { name: "clip_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-view-quiet-hours-settings", "Allows users to view workspace quiet hours settings"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "quiet_hours_settings",
    action: "view",
  },
  {
    permission: permissions.editQuietHoursSettings,
    display_name: t("edit-quiet-hours-settings-display-name", "Edit Quiet Hours Settings"),
    feature_flipper: [
      { name: "workspace_quiet_hours", match_value: true },
      { name: "clip_granular_permissions_all", match_value: true },
    ],
    helper_text: t("helper-text-edit-quiet-hours-settings", "Allows users to edit workspace quiet hours settings"),
    enabled_for_territories: false,
    granular_permission_of: permissions.manageApps,
    resource: "quiet_hours_settings",
    action: "edit",
  },
  {
    permission: permissions.editDecisioningStudioAgents,
    display_name: t("edit-decisioning-studio-agents-display-name", "Edit Decisioning Studio Agents"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "offerfit_customer_interface_granular_permission_basic_read_only", match_value: true },
    ],
    helper_text: t("helper-text-edit-decisioning-studio-agents", "Allows users to edit Decisioning Studio agents"),
    definition: t("edit-decisioning-studio-agents-definition", "Create and update new Decisioning Studio Agents"),
    enabled_for_territories: false,
    granular_permission_of: permissions.basicReadOnly,
    resource: "decisioning_studio_agents",
    action: "edit",
  },
  {
    permission: permissions.editDecisioningStudioCreativeVariants,
    display_name: t(
      "edit-decisioning-studio-creative-variants-display-name",
      "Edit Decisioning Studio Creative Variants"
    ),
    feature_flipper: null,
    helper_text: t(
      "helper-text-edit-decisioning-studio-creative-variants",
      "Allows users to edit Decisioning Studio creative variants"
    ),
    enabled_for_territories: false,
    resource: "decisioning_studio_creative_variants",
    action: "edit",
  },
  {
    permission: permissions.editTechnologyPartners,
    display_name: t("edit-technology-partners-display-name", "Edit Technology Partners"),
    feature_flipper: [
      { name: "clip_granular_permissions_all", match_value: true },
      { name: "pp_granular_permission_external_integrations", match_value: true },
    ],
    helper_text: t("helper-text-edit-technology-partners", "Allows users to edit technology partners"),
    enabled_for_territories: false,
    granular_permission_of: permissions.externalIntegrations,
    resource: "technology_partners",
    action: "edit",
  },
  {
    permission: permissions.viewConversationalWorkflows,
    display_name: t("view-conversational-workflows-display-name", "View Conversational Workflows"),
    definition: t("view-conversational-workflows-definition", "Allows users to view conversational workflows"),
    feature_flipper: [{ name: "agent_console_conversational", match_value: true }],
    helper_text: t("view-conversational-workflows-definition", "Allows users to view conversational workflows"),
    enabled_for_territories: true,
    resource: "conversational_workflows",
    action: "view",
  },
  {
    permission: permissions.editConversationalWorkflows,
    display_name: t("edit-conversational-workflows-display-name", "Edit Conversational Workflows"),
    definition: t("edit-conversational-workflows-definition", "Allows users to edit conversational workflows"),
    feature_flipper: [{ name: "agent_console_conversational", match_value: true }],
    helper_text: t("edit-conversational-workflows-definition", "Allows users to edit conversational workflows"),
    enabled_for_territories: true,
    resource: "conversational_workflows",
    action: "edit",
  },
  {
    permission: permissions.viewConversationalRouting,
    display_name: t("view-conversational-routing-display-name", "View Conversational Routing"),
    definition: t(
      "view-conversational-routing-definition",
      "Allows users to view conversational routing settings."
    ),
    feature_flipper: [{ name: "agent_console_conversational", match_value: true }],
    helper_text: t(
      "helper-text-view-conversational-routing",
      "Allows users to view conversational routing settings"
    ),
    enabled_for_territories: true,
    resource: "conversational_routing",
    action: "view",
  },
  {
    permission: permissions.editConversationalRouting,
    display_name: t("edit-conversational-routing-display-name", "Edit Conversational Routing"),
    definition: t(
      "edit-conversational-routing-definition",
      "Allows users to edit conversational routing settings."
    ),
    feature_flipper: [{ name: "agent_console_conversational", match_value: true }],
    helper_text: t(
      "helper-text-edit-conversational-routing",
      "Allows users to edit conversational routing settings"
    ),
    enabled_for_territories: true,
    resource: "conversational_routing",
    action: "edit",
  },
  {
    permission: permissions.viewKnowledgeSources,
    display_name: t("view-knowledge-sources-display-name", "View Knowledge Sources"),
    definition: t("view-knowledge-sources-definition", "Allows users to view knowledge sources for AI agents."),
    feature_flipper: [{ name: "agent_console_knowledge_source", match_value: true }],
    helper_text: t("helper-text-view-knowledge-sources", "Allows users to view knowledge sources for AI agents"),
    enabled_for_territories: true,
    resource: "knowledge_sources",
    action: "view",
  },
  {
    permission: permissions.editKnowledgeSources,
    display_name: t("edit-knowledge-sources-display-name", "Edit Knowledge Sources"),
    definition: t(
      "edit-knowledge-sources-definition",
      "Allows users to create and edit knowledge sources for AI agents."
    ),
    feature_flipper: [{ name: "agent_console_knowledge_source", match_value: true }],
    helper_text: t(
      "helper-text-edit-knowledge-sources",
      "Allows users to create and edit knowledge sources for AI agents"
    ),
    enabled_for_territories: true,
    resource: "knowledge_sources",
    action: "edit",
  },
  {
    permission: permissions.deleteKnowledgeSources,
    display_name: t("delete-knowledge-sources-display-name", "Delete Knowledge Sources"),
    definition: t("delete-knowledge-sources-definition", "Allows users to delete knowledge sources for AI agents."),
    feature_flipper: [{ name: "agent_console_knowledge_source", match_value: true }],
    helper_text: t(
      "helper-text-delete-knowledge-sources",
      "Allows users to delete knowledge sources for AI agents"
    ),
    enabled_for_territories: true,
    resource: "knowledge_sources",
    action: "delete",
  },
  {
    permission: permissions.viewAudiencePopulation,
    display_name: t("view-audience-population-display-name", "View Audience Population"),
    feature_flipper: [{ name: "ut_view_audience_population_permission", match_value: true }],
    helper_text: t(
      "helper-text-view-audience-population",
      "Allows users to view audience size, population, and reachable user counts on campaigns, Canvases, and segments"
    ),
    definition: t(
      "view-audience-population-definition",
      "Allows users to view audience size and reachable user counts across campaigns, Canvases, and segments."
    ),
    enabled_for_territories: true,
    resource: "audience_population",
    action: "view",
  },
  {
    permission: permissions.viewCustomObjects,
    display_name: t("view-custom-objects-display-name", "View Custom Objects"),
    feature_flipper: [{ name: "b2b_ea", match_value: true }],
    helper_text: t("helper-text-view-custom-objects", "Allows users to view custom objects"),
    enabled_for_territories: false,
    resource: "custom_objects",
    action: "view",
    definition: t("view-custom-objects-definition", "View custom objects without making changes."),
  },
  {
    permission: permissions.editCustomObjects,
    display_name: t("edit-custom-objects-display-name", "Edit Custom Objects"),
    feature_flipper: [{ name: "b2b_ea", match_value: true }],
    helper_text: t("helper-text-edit-custom-objects", "Allows users to edit custom objects"),
    enabled_for_territories: false,
    resource: "custom_objects",
    action: "edit",
    definition: t("edit-custom-objects-definition", "Create and update custom objects."),
  },
  {
    permission: permissions.deleteCustomObjects,
    display_name: t("delete-custom-objects-display-name", "Delete Custom Objects"),
    feature_flipper: [{ name: "b2b_ea", match_value: true }],
    helper_text: t("helper-text-delete-custom-objects", "Allows users to delete custom objects"),
    enabled_for_territories: false,
    resource: "custom_objects",
    action: "delete",
    definition: t("delete-custom-objects-definition", "Permanently delete custom objects."),
  },
  {
    permission: permissions.exportCustomObjects,
    display_name: t("export-custom-objects-display-name", "Export Custom Objects"),
    feature_flipper: [{ name: "b2b_ea", match_value: true }],
    helper_text: t("helper-text-export-custom-objects", "Allows users to export custom objects"),
    enabled_for_territories: false,
    resource: "custom_objects",
    action: "export",
    definition: t("export-custom-objects-definition", "Download custom objects from the dashboard."),
  },
];

// Update the permission ownership matrix after adding permissions: https://confluence.braze.com/x/e4BWCg
